<template>
    <!-- ══ SIDEBAR OVERLAY ══ -->
    <!-- Pas de voile en mode inline : le panneau occupe un onglet, il n'y a
         rien a masquer derriere lui. -->
    <div v-if="!inline" class="eflo-sidebar-overlay" :class="{ open: isOpen }" @click="close" />

    <!-- ══ MESSAGES SIDEBAR ══ -->
    <div class="eflo-msg-sidebar" :class="{ open: isOpen, 'is-inline': inline }">

        <!-- Header -->
        <div class="ms-header">
            <div class="ms-header-icon">💬</div>
            <div style="flex:1;display: flex;align-items: center;justify-content: space-between;">
                <div>
                    <div class="ms-title">Messages &amp; Alertes</div>
                        <div class="ms-subtitle"
                        v-if="orderExternalReference || orderReference || orderLabel"
                            :class="{ 'ms-subtitle-link': orderId }" @click.stop="openOrderTab()"
                            :title="orderId ? 'Ouvrir le dossier dans un nouvel onglet' : ''">
                            {{ orderExternalReference || orderReference }}
                            <span v-if="orderLabel"> · {{ orderLabel }}</span>
                            <span v-if="orderId" class="ms-subtitle-ext">↗</span>
                        </div>
                </div>
                <button class="ms-new-btn" @click="openCompose()"><span
                        style="    color: rgb(246 174 18);font-weight: bold;font-size: 2em;">+</span> Nouveau message /
                    alerte</button>
            </div>
            <button v-if="!inline" class="ms-close" @click="close">✕</button>
        </div>

        <!-- Tabs -->
        <div class="ms-tabs">
            <div v-for="tab in tabs" :key="tab.key" class="mstab" :class="{ active: activeTab === tab.key }"
                @click="activeTab = tab.key">
                <span v-if="tab.icon">{{ tab.icon }}</span>{{ tab.label }}
                <span class="cnt" :class="{ 'cnt-red': tab.key === 'alerts' }">
                    {{ tabCount(tab.key) }}
                </span>
            </div>
        </div>

        <!-- Read / Unread filter -->
        <div class="ms-read-filter">
            <button v-for="f in visibleReadFilters" :key="f.key" class="rf-btn"
                :class="{ active: readFilter === f.key }"
                :data-testid="`omp-read-filter-${f.key}`" @click="readFilter = f.key">
                {{ f.label }}
                <span v-if="f.key === 'unread' && unreadCount > 0" class="rf-badge">{{ unreadCount }}</span>
            </button>
            <div class="rf-tools">
                <div class="rf-sort-select-wrap" title="Critère de tri">
                    <span class="material-icons rf-sort-icon">sort</span>
                    <select class="rf-sort-select" v-model="sortBy">
                        <option value="date">Date</option>
                        <option value="priority">Priorité</option>
                    </select>
                </div>
                <button class="rf-icon-btn" @click="toggleSortDirection"
                    :title="`Ordre (${sortDirection === 'desc' ? 'descendant' : 'ascendant'})`">
                    <span class="material-icons">{{ sortDirection === "desc" ? "arrow_downward" : "arrow_upward"
                    }}</span>
                </button>
                <button class="rf-icon-btn danger" :disabled="deletingAll" @click="deleteAllFilteredMessages"
                    title="Supprimer tous les messages et alertes affichés">
                    <span class="material-icons">delete_sweep</span>
                </button>
            </div>
        </div>

        <!-- Search -->
        <div class="ms-search">
            <input type="text" v-model="searchInput" placeholder="Rechercher dans les messages..." />
        </div>

        <!-- Message list -->
        <div class="ms-list">
            <template v-if="filteredMessages.length > 0">
                <div v-for="(msg, idx) in filteredMessages" :key="msg.resourceUid || idx" class="mc"
                    data-testid="omp-message-row"
                    :class="[msgCardBorderClass(msg), { 'mc-unread': threadHasUnread(msg), 'mc-deleted': isDeletedItem(msg), 'mc-selected': (selectedMessage && selectedMessage.resourceUid === msg.resourceUid) || (selectedEmail && selectedEmail.resourceUid === msg.resourceUid) }]"
                    @click="openDetail(msg)">
                    <div class="mc-head">
                        <div class="mc-avatar" :class="avatarColorClass(msg)"> {{ senderInitials(msg) }}</div>
                        <div class="mc-info">
                            <div class="mc-subject">{{ subjectText(msg) }}
                                <span class="ms-subtitle"
                                    v-for="ref in msg.metadata.find((m: any) => m.key === 'Entity_Reference')?.value?.split(',')"
                                    :key="ref"
                                    v-if="!orderId"
                                    :class="{ 'ms-subtitle-link': orderId }"
                                    @click.stop="openOrderTab(ref)"
                                    title="Ouvrir le dossier dans un nouvel onglet">
                                    {{ref}},
                                </span>
                            </div>
                            <div class="mc-meta">
                                <span>DE : {{ msg.sender }}</span>
                                <span v-if="recipientLabel(msg)">| À : {{ recipientLabel(msg) }}</span>
                                <span v-else-if="channelText(msg)">→ {{ channelText(msg) }}</span>
                            </div>
                        </div>
                        <div class="mc-actions">
                            <button v-if="!msg._isEmail && isThreadRoot(msg) && getReplyCount(msg) > 0"
                                class="mc-reply-badge" @click.stop="openDetail(msg)" title="Voir la conversation">
                                💬 {{ getReplyCount(msg) }}
                            </button>
                            <!-- seules les alertes sont supprimables : un message/mémo ne peut pas l'être
                                 (pas de DELETE côté odm-contactmechanism, le statut passe juste à CLOSED) -->
                            <button v-if="msg._isAlert && !isDeletedItem(msg)" class="mc-delete-btn"
                                data-testid="omp-message-delete" @click="deleteMessage(msg, $event)" title="Supprimer">
                                🗑️
                            </button>
                        </div>
                        <div class="mc-time">{{ formatDate(msg.communicationDate) }}</div>
                    </div>
                    <div v-if="!msg._isEmail" class="mc-excerpt">{{ bodyExcerpt(msg) }}</div>
                    <div v-if="isDeletedItem(msg)" class="mc-deleted-note" data-testid="omp-deleted-note">
                        Supprimé<template v-if="deletedByOf(msg)"> par : {{ deletedByOf(msg) }}</template><template
                            v-if="deletedAtOf(msg)"> le {{ formatDate(deletedAtOf(msg), true) }}</template>
                    </div>
                    <div v-if="showMessageOrderKpiStrip(msg)" class="mc-kpi-strip" data-testid="omp-message-kpis">
                        <span v-for="pill in messageOrderKpiPills(msg)" :key="pill.key" class="mc-kpi-pill"
                            :title="`${pill.label} : ${pill.value}`">
                            <span class="mc-kpi-pill-lbl">{{ pill.label }}</span>
                            <span class="mc-kpi-pill-val">{{ pill.value }}</span>
                        </span>
                    </div>
                    <div class="mc-footer">
                        <span v-if="isDeletedItem(msg)" class="tag tg-deleted">Supprimé</span>
                        <span v-if="!msg._isAlert" class="tag" :class="typeTagClass(msg)">{{ typeLabel(msg) }}</span>
                        <span v-if="msg.status && !msg._isAlert" class="tag" :class="statusTagClass(msg)">{{
                            statusLabel(msg) }}</span>
                        <template v-if="alertBadgeLabels(msg).length > 0">
                            <span v-for="alert in alertBadgeLabels(msg)" :key="alert" class="tag tg-alert">
                                {{ alert }}
                            </span>
                        </template>
                        <span v-if="attachmentCount(msg) > 0"
                            style="margin-left:auto;font-size:11px;color:var(--eflo-text-3)">
                            📎 {{ attachmentCount(msg) }}
                        </span>
                    </div>
                </div>
            </template>
            <div v-else class="ms-empty">Aucun message pour ce dossier</div>
        </div>

    </div>

    <!-- ══ DETAIL DRAWER ══ -->
    <div class="eflo-detail-drawer" data-testid="omp-detail-drawer" :class="{ open: !!selectedMessage }">
        <template v-if="selectedMessage">
            <div class="dd-header">
                <span class="dd-icon">{{ msgIcon(selectedMessage) }}</span>
                <div class="dd-header-info">
                    <span class="dd-subject">{{ subjectText(selectedMessage) }}</span>
                    <span class="dd-header-meta">
                        <span class="tag" :class="typeTagClass(selectedMessage)">{{ typeLabel(selectedMessage) }}</span>
                        <span v-if="selectedMessage.status" class="tag" :class="statusTagClass(selectedMessage)">{{
                            statusLabel(selectedMessage) }}</span>
                    </span>
                </div>
                <button class="ms-close" @click="closeDetail">✕</button>
            </div>

            <!-- Conversation thread (Messenger style) -->
            <div class="dd-thread" ref="threadRef">
                <div v-if="loadingThread" class="dd-thread-loading">
                    <div class="dd-loading-spinner"></div>
                    <span>Chargement de la conversation...</span>
                </div>
                <template v-else-if="messageThread(selectedMessage).length > 0">
                    <div v-for="(msg, idx) in messageThread(selectedMessage)" :key="msg.resourceUid || idx"
                        class="dd-bubble-row"
                        :class="{ 'bubble-row-me': isCurrentUser(msg), 'bubble-row-them': !isCurrentUser(msg) }">

                        <div v-if="showDateSeparator(messageThread(selectedMessage), idx)" class="dd-date-sep">
                            <span>{{ formatDateSep(msg.communicationDate) }}</span>
                        </div>

                        <div v-if="!isCurrentUser(msg)" class="dd-bubble-avatar" :class="bubbleAvatarClass(msg)">
                            {{ senderInitials(msg) }}
                        </div>

                        <div class="dd-bubble-wrap">
                            <div class="dd-bubble-meta-top" v-if="!isCurrentUser(msg)">
                                <span class="dd-sender-name">{{ msg.sender }}</span>
                                <span v-if="msg._threadType === 'reply'" class="dd-event-badge ev-reply">↩
                                    Réponse</span>
                                <span v-if="msg._threadType === 'transfer'" class="dd-event-badge ev-transfer">↪
                                    Transfert</span>
                            </div>
                            <div class="dd-bubble-meta-top align-right" v-else>
                                <span v-if="msg._threadType === 'reply'" class="dd-event-badge ev-reply">↩
                                    Réponse</span>
                                <span v-if="msg._threadType === 'transfer'" class="dd-event-badge ev-transfer">↪
                                    Transfert</span>
                                <span class="dd-sender-name">Vous</span>
                            </div>

                            <div v-if="msg._refSubject" class="dd-bubble-ref">
                                <div class="dd-bubble-ref-bar"></div>
                                <div>
                                    <div class="dd-bubble-ref-label">{{ threadRefLabel(msg) }}</div>
                                    <div class="dd-bubble-ref-text">{{ msg._refSubject }}</div>
                                </div>
                            </div>

                            <div class="dd-bubble" :class="[bubbleClass(msg), { 'bubble-alert': isAlert(msg) }]">
                                <div class="dd-bubble-body">{{ msg.body || msg.content }}</div>
                                <div v-if="msg.attachments && msg.attachments.length > 0" class="dd-bubble-attachments">
                                    <div v-for="(doc, di) in msg.attachments" :key="doc.resourceUid || di"
                                        class="dd-bubble-doc">
                                        📎 {{ doc.name || doc.resourceUid }}
                                    </div>
                                </div>
                            </div>

                            <div class="dd-bubble-time" :class="{ 'time-right': isCurrentUser(msg) }">
                                {{ formatDate(msg.communicationDate, false) }}
                                <span v-if="isCurrentUser(msg)" class="dd-read-tick"
                                    :class="{ 'tick-read': isRead(msg) }">
                                    {{ isRead(msg) ? '✓✓' : '✓' }}
                                </span>
                            </div>
                        </div>
                    </div>
                </template>

                <div v-else class="dd-thread-empty">
                    <div class="dd-thread-empty-icon">💬</div>
                    <div>Aucun historique disponible</div>
                </div>
            </div>

            <!-- Actions bar -->
            <div class="dd-actions-bar">
                <div class="dd-actions-row"
                    v-if="selectedMessage.type?.resourceUid !== MESSAGE_TYPE.MEMO && !isTreated(selectedMessage)">
                    <button class="dd-action-btn primary" @click="startReply(selectedMessage)">↩ Répondre</button>

                </div>

                <div class="dd-actions-row"
                    v-if="selectedMessage.type?.resourceUid !== MESSAGE_TYPE.MEMO && !isTreated(selectedMessage)">

                    <button class="dd-action-btn" @click="startTransfer(selectedMessage)">↪ Transférer</button>

                    <button v-if="canMarkStatus(selectedMessage) && canMarkRead(selectedMessage)" class="dd-action-btn"
                        :disabled="markingRead" @click="markAsRead(selectedMessage)">
                        👁 Marquer Lu
                    </button>
                    <button v-if="canMarkStatus(selectedMessage)" class="dd-action-btn" :disabled="markingTreated"
                        @click="markAsTreated(selectedMessage)">
                        ✅ Traité
                    </button>
                </div>
            </div>
        </template>
    </div>

    <!-- ══ EMAIL POPUP ══ -->
    <div class="eflo-overlay" :class="{ open: !!selectedEmail }" @click.self="closeEmail">
        <div class="eflo-email-modal" v-if="selectedEmail" data-testid="omp-email-modal">
            <div class="em-header">
                <div class="em-icon">📧</div>
                <div class="em-header-info">
                    <span class="em-subject">{{ subjectText(selectedEmail) }}</span>
                    <span class="em-ref" v-if="selectedEmail._reference">
                        {{ selectedEmail._externalReference || selectedEmail._reference }}
                    </span>
                </div>
                <button class="ms-close" @click="closeEmail">✕</button>
            </div>
            <div class="em-fields">
                <div class="em-field"><span class="em-lbl">De</span><span class="em-val">{{ selectedEmail.sender || "—"
                        }}</span></div>
                <div class="em-field"><span class="em-lbl">À</span><span class="em-val">{{ selectedEmail.recipient ||
                        "—"
                        }}</span></div>
                <div class="em-field" v-if="selectedEmail.copyRecipient"><span class="em-lbl">Cc</span><span
                        class="em-val">{{ selectedEmail.copyRecipient }}</span></div>
                <div class="em-field"><span class="em-lbl">Date</span><span class="em-val">{{
                    formatDate(selectedEmail.communicationDate, true) }}</span></div>
            </div>
            <div class="em-body">
                <!-- Corps HTML rendu dans une iframe sandboxée (aucun script exécuté) pour isoler le contenu. -->
                <iframe v-if="emailIsHtml(selectedEmail.body)" class="em-iframe" sandbox title="Contenu de l'email"
                    :srcdoc="selectedEmail.body"></iframe>
                <pre v-else class="em-plain">{{ selectedEmail.body }}</pre>
            </div>
        </div>
    </div>

    <!-- ══ COMPOSE MODAL ══ -->
    <div class="eflo-overlay" :class="{ open: showCompose }" @click.self="closeCompose">
        <div class="eflo-compose">

            <!-- Header -->
            <div class="cm-header">
                <div class="cm-icon" :style="composeIconStyle">{{ composeIconEmoji }}</div>
                <span class="cm-title">{{ composeTitle }}</span>

                <button class="cm-close" @click="closeCompose">✕</button>
            </div>

            <!-- ══ 2-COLUMN BODY ══ -->
            <div class="cm-body">

                <!-- ─── COLONNE GAUCHE ─── -->
                <div class="cm-col cm-col-left">

                    <!-- Sujet & Référence -->
                    <div class="form-sec">
                        <div class="form-sec-title">
                            <span class="fst-dot" style="background:var(--eflo-blue)"></span>
                            {{ isSmsMode ? "Type de message" : "Sujet & Référence" }}
                        </div>
                        <div class="form-sec-body">
                            <div class="form-row">
                                <div class="form-g">
                                    <div class="form-lbl">Type de message <span class="req">★</span></div>
                                    <div class="type-toggle">
                                        <div v-for="t in visibleMsgTypes" :key="t.key" class="tt-opt"
                                            :class="{ active: compose.msgType === t.key }"
                                            @click="selectMessageType(t.key)">
                                            {{ t.icon }} {{ t.label }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="form-g" v-if="compose.msgType === MESSAGE_TYPE.ALERT && !isSmsMode">
                                <div class="form-lbl">
                                    Motif d'alerte
                                    <span class="req">★</span>
                                </div>
                                <div class="fselect-wrap">
                                    <select class="fselect" v-model="selectedAlertReason">
                                        <option value="">Choisir un motif...</option>
                                        <option v-for="al in alertTypes" :key="al.key" :value="al.key">
                                            {{ al.label }}
                                        </option>
                                    </select>
                                    <button v-if="selectedAlertReason" class="fselect-clear"
                                        @click.stop="selectedAlertReason = ''">✕</button>
                                </div>
                            </div>
                            <div class="form-g" v-if="!isSmsMode">
                                <div class="form-lbl">Sujet <span class="req">★</span></div>
                                <div class="fselect-wrap">
                                    <select class="fselect" v-model="compose.subject">
                                        <option value="" disabled>Choisir un sujet...</option>
                                        <option v-for="s in subjectsList" :key="s.value || s" :value="s.value || s">
                                            {{ s.label || s }}
                                        </option>
                                    </select>
                                    <button v-if="compose.subject" class="fselect-clear"
                                        @click.stop="compose.subject = ''">✕</button>
                                </div>
                            </div>

                            <div class="form-g" v-if="compose.msgType !== MESSAGE_TYPE.MEMO && !isSmsMode">
                                <div class="form-lbl">Référence(s)</div>
                                <div class="ref-search-wrap">
                                    <input class="finput" type="text" v-model="refSearch"
                                        placeholder="Rechercher Offre, Tiers, Commande..." @input="onRefSearch"
                                        @focus="onRefInputFocus" @click="onRefInputFocus" autocomplete="off"
                                        autocorrect="off" autocapitalize="off" spellcheck="false" data-form-type="other"
                                        data-lpignore="true" />
                                    <div class="ref-dropdown"
                                        v-if="refDropOpen && (refFilteredOptions.length > 0 || refLoading)">
                                        <div v-if="refLoading" class="ref-opt ref-loading">⏳ Recherche...</div>
                                        <template v-else>
                                            <div v-for="opt in refFilteredOptions"
                                                :key="refLabel(opt) + refContext(opt)" class="ref-opt"
                                                @mousedown.prevent="selectRef(opt)">
                                                <span class="ref-badge" :class="refBadgeClass(opt)">{{ refContext(opt)
                                                    }}</span>
                                                <span class="ref-id">{{ refLabel(opt) }}</span>
                                            </div>
                                        </template>
                                    </div>
                                    <div class="ref-dropdown"
                                        v-if="refDropOpen && !refFilteredOptions.length && refSearch.length >= 2 && !refLoading">
                                        <div class="ref-opt ref-empty">Aucun résultat</div>
                                    </div>
                                </div>

                                <div class="ref-chips" v-if="compose.selectedRefs.length > 0">
                                    <div v-for="(ref, ri) in compose.selectedRefs" :key="ri" class="ref-chip">
                                        <span class="ref-badge" :class="refBadgeClass(ref)">{{ refContext(ref) }}</span>
                                        <span>{{ refLabel(ref) }}</span>
                                        <button class="ref-chip-remove" @click="removeRef(ri)">✕</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Destinataires -->
                    <div class="form-sec" v-if="compose.msgType !== MESSAGE_TYPE.MEMO">
                        <div class="form-sec-title">
                            <span class="fst-dot" style="background:var(--eflo-teal)"></span>
                            Destinataires
                        </div>
                        <div class="form-sec-body">
                            <div class="form-g">
                                <div class="form-lbl">Destinataires <span class="req">★</span></div>
                                <div v-if="!isSmsMode && filteredIntervenantBundles.length > 0"
                                    class="recip-intervenants-strip">
                                    <div class="recip-intervenants-strip-title">Intervenants sur la commande</div>
                                    <div v-for="b in filteredIntervenantBundles" :key="recipientSuggestionKey(b)"
                                        class="recip-intervenant-card">
                                        <div class="recip-intervenant-card-info">
                                            <span class="recip-badge recip-badge-order-party">{{
                                                orderPartyRoleLabel(b.orderPartyRole) }}</span>
                                            <div class="recip-meta">
                                                <span class="recip-name">{{ b.name || b.emailOption?.email }}</span>
                                                <span v-if="b.emailOption?.email" class="recip-sub">{{
                                                    b.emailOption.email
                                                }}</span>
                                            </div>
                                        </div>
                                        <div class="recip-intervenant-card-cta">
                                            <button v-if="b.userOption" type="button"
                                                class="recip-cta recip-cta-message"
                                                @click="selectIntervenantChannel(b, 'user')">Message</button>
                                            <button type="button" class="recip-cta recip-cta-mail"
                                                @click="selectIntervenantChannel(b, 'email')">Mail</button>
                                        </div>
                                    </div>
                                </div>
                                <div class="recip-filter-tabs" v-if="!isSmsMode">
                                    <span class="recip-filter-tab" :class="{ active: recipientFilter === 'All' }"
                                        @click="recipientFilter = 'All'">Tous</span>
                                    <span class="recip-filter-tab" :class="{ active: recipientFilter === 'User' }"
                                        @click="recipientFilter = 'User'">Utilisateurs</span>
                                    <span class="recip-filter-tab" :class="{ active: recipientFilter === 'Group' }"
                                        @click="recipientFilter = 'Group'">Groupes</span>
                                </div>
                                <div class="recip-search-wrap">
                                    <input class="finput" type="text" v-model="recipientSearch"
                                        :placeholder="isSmsMode ? 'Rechercher un utilisateur ou saisir un numéro (Entrée pour ajouter)...' : 'Rechercher un utilisateur ou saisir un email (Entrée pour ajouter)...'"
                                        @focus="onRecipInputFocus" @click="onRecipInputFocus"
                                        @keydown.enter.prevent="addRecipientFromSearch()" autocomplete="off"
                                        autocorrect="off" autocapitalize="off" spellcheck="false" data-form-type="other"
                                        data-lpignore="true" name="recipient-search-no-autofill" />
                                    <div class="recip-dropdown"
                                        v-if="recipDropOpen && filteredRecipientOptions.length > 0">
                                        <div v-for="r in filteredRecipientOptions" :key="recipientSuggestionKey(r)"
                                            class="recip-opt">
                                            <div class="recip-opt-simple" @mousedown.prevent="selectRecipient(r)">
                                                <span class="recip-badge" :class="getRecipientTypeClass(r)">{{
                                                    getRecipientTypeLabel(r) }}</span>
                                                <div class="recip-meta">
                                                    <span class="recip-name">{{ getRecipientDisplayName(r) }}</span>
                                                    <span
                                                        v-if="!isSmsMode && r.isOrderPartyLinkedUser && r.orderPartyRole"
                                                        class="recip-sub">{{ orderPartyRoleLabel(r.orderPartyRole) }} ·
                                                        {{
                                                            r.email || "" }}</span>
                                                    <span v-if="!isSmsMode && r.isFromOrderParty && r.email && r.name"
                                                        class="recip-sub">{{ r.email }}</span>
                                                    <span v-if="isSmsMode" class="recip-sub"
                                                        :class="{ 'recip-sub-missing': !getPrimaryRecipientPhone(r) }">
                                                        {{ recipientPhoneHint(r) }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="recip-dropdown"
                                        v-if="recipDropOpen && filteredRecipientOptions.length === 0 && recipientSearch.length >= 1">
                                        <div class="recip-opt recip-empty">Aucun résultat</div>
                                    </div>
                                </div>
                                <div class="recip-chips" v-if="compose.selectedRecipients.length > 0">
                                    <div v-for="(r, ri) in compose.selectedRecipients" :key="ri" class="recip-chip">
                                        <span class="recip-badge" :class="getRecipientTypeClass(r)">{{
                                            getRecipientTypeLabel(r) }}</span>
                                        <div class="recip-meta">
                                            <span>{{ getRecipientDisplayName(r) }}</span>
                                            <span v-if="!isSmsMode && r.isOrderPartyLinkedUser && r.orderPartyRole"
                                                class="recip-sub">{{ orderPartyRoleLabel(r.orderPartyRole) }} · {{
                                                    r.email || "" }}</span>
                                            <span v-if="!isSmsMode && r.isFromOrderParty && r.email && r.name"
                                                class="recip-sub">{{ r.email }}</span>
                                            <span v-if="isSmsMode" class="recip-sub"
                                                :class="{ 'recip-sub-missing': !getPrimaryRecipientPhone(r) && !r.isPhone }">
                                                {{ recipientPhoneHint(r) }}
                                            </span>
                                        </div>
                                        <button class="recip-chip-remove" @click="removeRecipient(ri)">✕</button>
                                    </div>
                                </div>
                                <div v-if="!hasRecipientDirectory" class="recip-empty-msg">
                                    {{ isSmsMode ? "Aucun destinataire disponible. Saisissez un numéro manuellement." :
                                        "Aucun destinataire disponible" }}
                                </div>
                            </div>
                        </div>
                    </div>



                </div><!-- /cm-col-left -->

                <!-- ─── COLONNE DROITE ─── -->
                <div class="cm-col cm-col-right">

                    <!-- Contenu -->
                    <div class="form-sec">
                        <div class="form-sec-title">
                            <span class="fst-dot" style="background:var(--eflo-purple)"></span>
                            Contenu
                        </div>
                        <div class="form-sec-body">
                            <div v-if="compose.replyTo || compose.transferOf" class="compose-context-banner">
                                <span v-if="compose.replyTo">
                                    ↩ Réponse à : <strong>{{ subjectText(compose.replyTo) }}</strong>
                                </span>
                                <span v-if="compose.transferOf">
                                    ↪ Transfert de : <strong>{{ subjectText(compose.transferOf) }}</strong>
                                </span>
                                <button class="context-clear"
                                    @click="compose.replyTo = null; compose.transferOf = null">✕</button>
                            </div>
                            <div class="form-g">
                                <div class="form-lbl">Message <span class="req">★</span></div>
                                <textarea class="ftextarea" v-model="compose.body"
                                    placeholder="Saisir le contenu du message..." :rows="6"></textarea>
                            </div>
                        </div>
                    </div>

                    <!-- Documents joints -->
                    <div class="form-sec" v-if="!isSmsMode">
                        <div class="form-sec-title">
                            <span class="fst-dot" style="background:var(--eflo-orange)"></span>
                            Documents joints

                        </div>
                        <div class="form-sec-body">
                            <div class="file-hint">
                                Types acceptes: PDF,JPG, JPEG, PNG - Taille max: 10 Mo.
                            </div>
                            <!-- Documents from order -->
                            <div class="form-g"
                                v-if="orderReference && flattenedDocuments && flattenedDocuments.length > 0">
                                <div class="form-lbl">Depuis le dossier</div>

                                <!-- Search input -->
                                <div class="doc-search-wrap">
                                    <input class="finput" type="text" v-model="docSearch"
                                        placeholder="Rechercher un document..." />
                                </div>

                                <!-- Scrollable doc list -->
                                <div class="doc-items-scroll">
                                    <template v-if="filteredFlattenedDocuments.length > 0">
                                        <div v-for="doc in filteredFlattenedDocuments" :key="doc.resourceUid"
                                            class="doc-item"
                                            :class="{ on: compose.selectedDocs.includes(doc.resourceUid) }"
                                            @click="toggleDocItem(doc.resourceUid)">
                                            <span>📄</span>
                                            <div class="doc-item-info">
                                                <span class="doc-name">{{ doc.displayName }}</span>
                                                <span class="doc-process">{{ doc.processLabel || 'Document' }}</span>
                                            </div>
                                            <div class="doc-chk">
                                                {{ compose.selectedDocs.includes(doc.resourceUid) ? '✓' : '' }}
                                            </div>
                                        </div>
                                    </template>
                                    <div v-else class="doc-empty">Aucun document trouvé</div>
                                </div>

                                <!-- Selected chips -->
                                <div class="ref-chips" v-if="compose.selectedDocs.length > 0">
                                    <div v-for="uid in compose.selectedDocs" :key="uid" class="ref-chip">
                                        <span>📎</span>
                                        <span>{{flattenedDocuments.find(d => d.resourceUid === uid)?.displayName || uid
                                            }}</span>
                                        <button class="ref-chip-remove" @click="removeDocument(uid)">✕</button>
                                    </div>
                                </div>
                            </div>

                            <!-- File upload -->
                            <div class="form-g">
                                <div class="form-lbl">Joindre un fichier</div>
                                <input class="finput" type="file" style="padding:5px 11px;cursor:pointer"
                                    @change="onFileChange" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt" />

                                <div v-if="compose.attachedFile" class="mt-2"
                                    style="font-size:12px;color:var(--eflo-text-2)">
                                    📎 {{ compose.attachedFile.name }}
                                </div>
                            </div>

                        </div>
                    </div>

                </div><!-- /cm-col-right -->

            </div><!-- /cm-body -->

            <!-- Sending overlay -->
            <div class="cm-sending" v-if="sendingMessage">
                <div class="cm-sending-inner">
                    <div class="cm-spinner"></div>
                    <span>Envoi en cours...</span>
                </div>
            </div>

            <!-- Footer -->
            <div class="cm-footer">
                <div class="cm-footer-left">
                    <span v-if="composeError" class="cm-error">⚠ {{ composeError }}</span>
                </div>
                <div class="cm-footer-right">
                    <button class="btn-cancel" @click="closeCompose">Annuler</button>
                    <button class="btn-send"
                        :class="{ 'alert-mode': compose.alertType.length > 0, 'important-mode': compose.important }"
                        :disabled="sendingMessage || !canSend" @click="sendMessage">
                        {{ sendBtnLabel }}
                    </button>
                </div>
            </div>

        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount, PropType } from "vue";
import store from "@/store";
import {
    MESSAGE_TYPE,
    clientAppMsg,
    messageTemplate,
    objectType_odmTransferReplyMsg,
    recipient,
    senderUid,
    subject as subjectConst,
    typeMsg,
} from "@/types";
import { convertToUON, translateGroupName } from "@/utils";
import { sendMailWithDocument } from "@/store/services/order/sendMail";
import { getAssociatedPartyInfo } from "@/store/services/order/orderService";
import { sendSms } from "@/store/services/task/contactMechanism";
import i18n from "@/i18n";
import router from "@/router";

const systemUid = "odm-contactmechanism";
const READ_STATES = new Set(["READ", "Lu", "read"]);
const TREATED_STATES = new Set(["CLOSED", "TREATED", "Traité", "treated", "closed"]);

function msgRootFromMessage(msg: any): string | null {
    // Conversation contract:
    // - first message: MSG_ROOT can be null/"null"
    // - first reply/transfer: MSG_ROOT becomes root message UID
    // - next exchanges: always reuse the same non-empty MSG_ROOT
    const metadata = Array.isArray(msg?.metadata) ? msg.metadata : [];
    const fromMetadata = metadata.find((m: any) => (m?.key || "").toLowerCase() === "msg_root")?.value;
    if (fromMetadata !== undefined && fromMetadata !== null) {
        const v = `${fromMetadata}`.trim();
        if (v !== "" && v.toLowerCase() !== "null" && v.toLowerCase() !== "undefined") return v;
    }

    const p = msg?.parameters || {};
    const fromParams =
        p.MSG_ROOT ??
        p.msg_root ??
        p.Msg_Root ??
        p.msgRoot ??
        p.MSGROOT;
    if (fromParams !== undefined && fromParams !== null) {
        const v = `${fromParams}`.trim();
        if (v !== "" && v.toLowerCase() !== "null" && v.toLowerCase() !== "undefined") return v;
    }

    return msg?.resourceUid || msg?.resourceuid || null;
}

function explicitMsgRootFromMessage(msg: any): string | null {
    // Strict read: returns only an explicit, usable MSG_ROOT value.
    // Used for grouping logic to avoid forcing UID fallback too early.
    const metadata = Array.isArray(msg?.metadata) ? msg.metadata : [];
    const fromMetadata = metadata.find((m: any) => (m?.key || "").toLowerCase() === "msg_root")?.value;
    if (fromMetadata !== undefined && fromMetadata !== null) {
        const v = `${fromMetadata}`.trim();
        if (v !== "" && v.toLowerCase() !== "null" && v.toLowerCase() !== "undefined") return v;
    }

    const p = msg?.parameters || {};
    const fromParams =
        p.MSG_ROOT ??
        p.msg_root ??
        p.Msg_Root ??
        p.msgRoot ??
        p.MSGROOT;
    if (fromParams !== undefined && fromParams !== null) {
        const v = `${fromParams}`.trim();
        if (v !== "" && v.toLowerCase() !== "null" && v.toLowerCase() !== "undefined") return v;
    }

    return null;
}

function msgRootForAction(msg: any): string | null {
    // If MSG_ROOT already exists on message (not null/empty), keep it.
    // Otherwise, first reply/transfer sets it to the treated message UID.
    const existing = msgRootFromMessage(msg);
    if (existing) return existing;
    return msg?.resourceUid || msg?.resourceuid || null;
}

function withMsgRoot(parameters: any, msgRoot: string | null): any {
    const p = parameters && typeof parameters === "object" ? parameters : {};
    const existing = p.MSG_ROOT ?? p.msg_root ?? p.Msg_Root ?? p.msgRoot ?? p.MSGROOT;
    if (existing !== undefined && existing !== null) {
        const v = `${existing}`.trim();
        if (v !== "" && v.toLowerCase() !== "null" && v.toLowerCase() !== "undefined") return p;
    }
    return { ...p, MSG_ROOT: msgRoot };
}

function getMessageParameters(msg: any): Record<string, any> {
    const raw = msg?.parameters;
    if (!raw) return {};
    if (typeof raw === "string") {
        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === "object" ? parsed : {};
        } catch {
            return {};
        }
    }
    if (typeof raw === "object") return raw;
    return {};
}

function messageFieldValue(msg: any, key: string): any {
    const metadata = Array.isArray(msg?.metadata) ? msg.metadata : [];
    const fromMetadata = metadata.find((m: any) => (m?.key || "").toLowerCase() === key.toLowerCase())?.value;
    if (fromMetadata !== undefined && fromMetadata !== null) return fromMetadata;

    const p = getMessageParameters(msg);
    if (p[key] !== undefined) return p[key];

    const lowerKey = key.toLowerCase();
    const keyMatch = Object.keys(p).find((k) => k.toLowerCase() === lowerKey);
    if (keyMatch) return p[keyMatch];
    return null;
}

function appendThreadMessagesFromGroup(mergedByUid: Record<string, any>, group: any): void {
    if (!group?.messages?.length) return;
    for (const m of group.messages) {
        const uid = m?.resourceUid || m?.resourceuid;
        if (!uid) continue;
        mergedByUid[uid] = { ...m, resourceUid: uid, _threadType: "", _refSubject: "" };
    }
}

function appendThreadMessagesFromHistoricPack(mergedByUid: Record<string, any>, messagePack: any[]): void {
    // Historic API keeps parent relation (reply/transfer); we preserve that
    // metadata to render messenger-like bubbles and references in the drawer.
    if (!Array.isArray(messagePack) || messagePack.length === 0) return;
    const sorted = [...messagePack].sort((a, b) => {
        const da = a.date ? new Date(a.date).getTime() : 0;
        const db = b.date ? new Date(b.date).getTime() : 0;
        return da - db;
    });
    for (const m of sorted) {
        let threadType = "";
        let refSubject = "";
        if (m.transfermsgparent) {
            threadType = "transfer";
            const parent = sorted.find((p: any) => p.resourceuid === m.transfermsgparent);
            refSubject = parent?.subject || "";
        } else if (m.replymsgparent) {
            threadType = "reply";
            const parent = sorted.find((p: any) => p.resourceuid === m.replymsgparent);
            refSubject = parent?.subject || "";
        }
        const uid = m.resourceuid || m.resourceUid;
        if (!uid) continue;
        mergedByUid[uid] = {
            ...mergedByUid[uid],
            resourceUid: uid,
            sender: m.sender,
            body: m.body,
            content: m.body,
            communicationDate: m.date,
            subject: m.subject,
            status: { resourceUid: m.status },
            recipient: m.recipientname || m.recipient_resourceuid,
            _threadType: threadType,
            _refSubject: refSubject,
            _isHistoric: true,
        };
    }
}

function sortThreadMessages(messages: any[]): any[] {
    return messages.sort((a: any, b: any) => {
        const da = a.communicationDate ? new Date(a.communicationDate).getTime() : 0;
        const db = b.communicationDate ? new Date(b.communicationDate).getTime() : 0;
        return da - db;
    });
}

function subjectText(m: any): string { return m?.subject?.resourceUid || m?.subject || "—"; }
function bodyExcerpt(m: any): string { return m?.body || m?.content || ""; }
function channelText(m: any): string { return m?.channel || m?.destType || ""; }
function attachmentCount(m: any): number { return m?.attachments?.length || 0; }
function getPriorityValue(m: any): number {
    if (typeof m?.priority === "number") return m.priority;
    if (typeof m?.priority === "boolean") return m.priority ? 1 : 0;
    if (m?._isAlert) return 3;
    return 0;
}
function formatDate(d: any, full = false): string {
    if (!d) return "";
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return "";
    if (full) return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const diff = (Date.now() - date.getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return "aujourd'hui";
    if (diff < 172800) return "hier";
    const days = Math.floor(diff / 86400);
    if (days < 30) return `il y a ${days}j`;
    return date.toLocaleDateString("fr-FR");
}
function isTreated(m: any): boolean { const s = m?.status?.resourceUid || m?.status || ""; return TREATED_STATES.has(s); }
function isRead(m: any): boolean { const s = m?.status?.resourceUid || m?.status || ""; return READ_STATES.has(s) || TREATED_STATES.has(s); }
function canMarkStatus(m: any): boolean { return !TREATED_STATES.has(m?.status?.resourceUid || m?.status || ""); }
function canMarkRead(m: any): boolean { return ["NEW", "SENT", "new", "sent", "Envoyé"].includes(m?.status?.resourceUid || m?.status || ""); }
function statusLabel(m: any): string {
    const s = m?.status?.resourceUid || m?.status || "";
    if (READ_STATES.has(s)) return "Lu";
    if (TREATED_STATES.has(s)) return "Traité";
    return "Envoyé";
}
function statusTagClass(m: any): string {
    const s = m?.status?.resourceUid || m?.status || "";
    if (READ_STATES.has(s)) return "tg-read";
    if (TREATED_STATES.has(s)) return "tg-treated";
    return "tg-sent";
}
function alertBadgeLabels(m: any): string[] {
    if (m._alertReasons && m._alertReasons.length > 0) return m._alertReasons;
    const val = m?.metadata?.find((meta: any) => meta.key === "Alert_Type")?.value || "";
    if (!val || val === "null") return [];
    return val.split(",").map((s: string) => s.trim()).filter(Boolean);
}

const MESSAGE_ORDER_KPI_DEFS: { key: string; label: string; money?: boolean }[] = [
    { key: "Entity_ExternalReference", label: "Référence externe" },
    { key: "Entity_AssociatedPartyName", label: "Client" },
    { key: "Entity_SellerName", label: "Vendeur" },
    { key: "Entity_NumFabrication", label: "N° de fabrication" },
    { key: "Entity_Brand", label: "Marque" },
    { key: "Entity_Ranges", label: "Gamme" },
    { key: "Entity_Versions", label: "Version" },
    { key: "Entity_PrixVehiculeHt", label: "Prix véh. HT", money: true },
    { key: "Entity_MargeVehiculeHt", label: "Marge véh. HT", money: true },
    { key: "Entity_PrixOptionsHt", label: "Options HT", money: true },
    { key: "Entity_PrixAchatOptionsHt", label: "Achat opt. HT", money: true },
    { key: "Entity_MargeOptionsHt", label: "Marge opt. HT", money: true },
    { key: "Entity_PrixAccessoiresHt", label: "Access. HT", money: true },
];

function isNullishMetaValue(v: unknown): boolean {
    if (v === undefined || v === null) return true;
    const s = String(v).trim();
    return s === "" || s.toLowerCase() === "null";
}

function formatKpiDisplayValue(raw: string, money?: boolean): string {
    if (!money) return raw;
    const n = Number(String(raw).replace(/\s/g, "").replace(",", "."));
    if (!Number.isFinite(n)) return raw;
    return `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n)} €`;
}

function messageMetadataValue(msg: any, key: string): any {
    const metadata = Array.isArray(msg?.metadata) ? msg.metadata : [];
    const entry = metadata.find((m: any) => (m?.key || "").toLowerCase() === key.toLowerCase());
    return entry?.value ?? null;
}

/**
 * Élément supprimé : alerte clôturée (alertValidity.until), ou message portant une trace de
 * suppression (deletedAt/deletedBy dans businessData ou dans ses paramètres).
 */
function isDeletedItem(m: any): boolean {
    if (m?._isDeleted) return true;
    return !!(m?.businessData?.deletedAt || m?.parameters?.deletedAt || messageMetadataValue(m, "deletedAt"));
}

/** Auteur / date de suppression, quelle que soit la source (alerte ou message). */
function deletedByOf(m: any): string {
    return m?._deletedBy || m?.businessData?.deletedBy || m?.parameters?.deletedBy || messageMetadataValue(m, "deletedBy") || "";
}
function deletedAtOf(m: any): string {
    return m?._deletedAt || m?.businessData?.deletedAt || m?.parameters?.deletedAt || messageMetadataValue(m, "deletedAt") || "";
}

/**
 * Alerte -> carte de message, pour réutiliser la liste et ses filtres.
 * `until` renseigné = alerte supprimée (clôturée) : on garde la trace de suppression pour l'afficher.
 */
function mapAlertToMessageCard(a: any): any {
    const deleted = !!a.alertValidity?.until;
    return {
        resourceUid: a.id,
        sender: a.createdBy,
        body: a.comment || "",
        content: a.comment || "",
        communicationDate: a.createdAt,
        subject: { resourceUid: a.reasons || "Alerte" },
        status: { resourceUid: "NEW" },
        type: { resourceUid: "ALERT", objectType: "odm.contactmechanism.messagetype" },
        priority: 3,
        _isAlert: true,
        _alertReasons: a.reasons ? String(a.reasons).split(",").map((r: string) => r.trim()) : [],
        _alertId: a.id,
        _orderId: a.orderId,
        _alertValidity: a.alertValidity,
        _isDeleted: deleted,
        _recipients: a.businessData?.recipients || "",
        _deletedBy: deleted ? a.businessData?.deletedBy || "" : "",
        _deletedAt: deleted ? a.businessData?.deletedAt || a.alertValidity?.until || "" : "",
        metadata: [
            { key: "Alert_Type", value: a.reasons || "" },
            { key: "Entity_Reference", value: a.orderReference || "" },
        ],
    };
}

function showMessageOrderKpiStrip(msg: any, panelOrderId: string): boolean {
    const isPortalClient = String(messageMetadataValue(msg, "portailClient") ?? "").toLowerCase() === "true";
    if (panelOrderId && !isPortalClient) return false;
    const entityRef = messageMetadataValue(msg, "Entity_Reference");
    if (isNullishMetaValue(entityRef) && !isPortalClient) return false;
    return messageOrderKpiPills(msg).length > 0;
}

function messageOrderKpiPills(msg: any): { key: string; label: string; value: string }[] {
    const pills: { key: string; label: string; value: string }[] = [];
    for (const def of MESSAGE_ORDER_KPI_DEFS) {
        const raw = messageMetadataValue(msg, def.key);
        if (isNullishMetaValue(raw)) continue;
        pills.push({
            key: def.key,
            label: def.label,
            value: formatKpiDisplayValue(String(raw).trim(), def.money),
        });
    }
    return pills;
}
function showDateSeparator(thread: any[], idx: number): boolean {
    if (idx === 0) return true;
    const prev = thread[idx - 1]; const curr = thread[idx];
    if (!prev.communicationDate || !curr.communicationDate) return false;
    return new Date(prev.communicationDate).toDateString() !== new Date(curr.communicationDate).toDateString();
}
function formatDateSep(d: any): string {
    if (!d) return "";
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return "";
    const today = new Date(); const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
    if (date.toDateString() === yesterday.toDateString()) return "Hier";
    return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}
function refContext(opt: any): string {
    if (opt.id && !opt.reference && !opt.referenceorder) return "Tiers";
    if (opt.referenceorder || opt.externalreference) return "Commande";
    if (opt.reference) return "Offre";
    return "Réf.";
}
function refLabel(opt: any): string {
    if (opt.externalreference) return opt.externalreference;
    if (opt.reference) return opt.reference;
    if (opt.id) return opt.id;
    if (opt.referenceorder) return opt.referenceorder;
    return "";
}
function threadRefLabel(msg: any): string {
    return msg._threadType === 'transfer' ? '↪ Transféré de' : '↩ En réponse à';
}
function refBadgeClass(opt: any): string {
    if (opt.id && !opt.reference && !opt.referenceorder) return "ref-badge-party";
    if (opt.referenceorder || opt.externalreference) return "ref-badge-order";
    return "ref-badge-offer";
}
function normRefKey(s: string | undefined | null): string {
    return String(s ?? "").trim().toLowerCase();
}
function isOrderRefOption(opt: any): boolean {
    return !!(opt?.referenceorder || opt?.externalreference);
}
function matchesOrderRefContext(opt: any, orderReference: string, externalRef: string, resourceUid: string): boolean {
    if (!isOrderRefOption(opt)) return false;
    const refKey = normRefKey(orderReference);
    const extKey = normRefKey(externalRef);
    const uidKey = normRefKey(resourceUid);
    if (refKey && normRefKey(opt.referenceorder) === refKey) return true;
    if (uidKey && (normRefKey(opt.resourceUid) === uidKey || normRefKey(opt.orderid) === uidKey || normRefKey(opt.orderId) === uidKey)) return true;
    if (extKey && normRefKey(opt.externalreference) === extKey) return true;
    return false;
}
function findOrderRefInOptions(options: any[], orderReference: string, externalRef: string, resourceUid: string): any | null {
    const list = options || [];
    const exact = list.find((opt) => matchesOrderRefContext(opt, orderReference, externalRef, resourceUid));
    if (exact) return exact;
    if (orderReference) {
        const byRef = list.find((opt) => isOrderRefOption(opt) && normRefKey(opt.referenceorder) === normRefKey(orderReference));
        if (byRef) return byRef;
    }
    return null;
}
function pickOrderRefFromSearchResults(results: any[], orderReference: string, externalRef: string, resourceUid: string): any | null {
    return findOrderRefInOptions((results || []).filter(isOrderRefOption), orderReference, externalRef, resourceUid);
}

const ORDER_ENTITY_CONTEXT_KEYS = [
    "Entity_AssociatedPartyName",
    "Entity_SellerName",
    "Entity_Brand",
    "Entity_Ranges",
    "Entity_Versions",
    "Entity_PrixVehiculeHt",
    "Entity_MargeVehiculeHt",
    "Entity_PrixOptionsHt",
    "Entity_PrixAchatOptionsHt",
    "Entity_MargeOptionsHt",
    "Entity_PrixAccessoiresHt",
] as const;

function paramStr(v: unknown): string | null {
    if (v === undefined || v === null) return null;
    const s = String(v).trim();
    return s === "" ? null : s;
}

function pickOrderRowField(row: any, ...keys: string[]): unknown {
    if (!row || typeof row !== "object") return undefined;
    for (const key of keys) {
        if (row[key] !== undefined && row[key] !== null) return row[key];
        const lower = key.toLowerCase();
        const match = Object.keys(row).find((k) => k.toLowerCase() === lower);
        if (match !== undefined && row[match] !== undefined && row[match] !== null) return row[match];
    }
    return undefined;
}

/** UID technique commande : resourceUid ODM (fiche) prioritaire, puis orderid issu de la recherche. */
function resolveOrderResourceUid(refObj: any, fallbackOrderId?: string, scopedOrderReference?: string): string {
    const resourceUid = pickOrderRowField(refObj, "resourceUid", "resourceuid");
    if (resourceUid !== undefined && resourceUid !== null && String(resourceUid).trim() !== "") {
        return String(resourceUid).trim();
    }

    const refKey = normRefKey(refObj?.referenceorder || refObj?.referenceOrder);
    const scopeKey = normRefKey(scopedOrderReference);
    const fallback = String(fallbackOrderId ?? "").trim();
    if (fallback && (!scopeKey || !refKey || refKey === scopeKey)) return fallback;

    const order = store.state.orderModule?.order;
    if (order?.resourceUid && refKey && refKey === normRefKey(order.reference)) {
        return String(order.resourceUid).trim();
    }

    const orderId = pickOrderRowField(refObj, "orderid", "orderId", "order_id");
    if (orderId !== undefined && orderId !== null && String(orderId).trim() !== "") {
        return String(orderId).trim();
    }
    return "";
}

function formatRangeLabel(raw: unknown): string {
    const s = String(raw ?? "").trim();
    if (!s) return "";
    if (s.includes("range.")) return s.substring(s.lastIndexOf(".") + 1).trim();
    return s;
}

function formatVersionLabel(raw: unknown): string {
    const s = String(raw ?? "").trim();
    if (!s) return "";
    if (s.includes("range.")) return s.substring(s.lastIndexOf("range") + 6).trim();
    return s;
}

function formatSellerName(row: any): string {
    const parts = [row?.sellerfirstname, row?.sellerfamilyname].map((x) => String(x ?? "").trim()).filter(Boolean);
    if (parts.length) return parts.join(" ");
    return String(row?.seller || row?.sellername || row?.sellercommercialname || "").trim();
}

function buildOrderContextParameters(row: any): Record<(typeof ORDER_ENTITY_CONTEXT_KEYS)[number], string | null> {
    const associatedpartyname = paramStr(pickOrderRowField(row, "associatedpartyname"));
    const sellerName = paramStr(formatSellerName(row));
    const brand = paramStr(pickOrderRowField(row, "brand"));
    const ranges = paramStr(formatRangeLabel(pickOrderRowField(row, "ranges", "range")));
    const versions = paramStr(formatVersionLabel(pickOrderRowField(row, "versions", "version")));
    const prixvehiculeht = paramStr(pickOrderRowField(row, "prixvehiculeht", "Prixvehiculeht"));
    const margevehiculeht = paramStr(pickOrderRowField(row, "margevehiculeht", "Margevehiculeht"));
    const prixoptionsht = paramStr(pickOrderRowField(row, "prixoptionsht", "Prixoptionsht"));
    const prixachatoptionsht = paramStr(pickOrderRowField(row, "prixachatoptionsht", "Prixachatoptionsht"));
    const margeoptionsht = paramStr(pickOrderRowField(row, "margeoptionsht", "Margeoptionsht"));
    const prixaccessoiresht = paramStr(pickOrderRowField(row, "prixaccessoiresht", "Prixaccessoiresht"));
    return {
        Entity_AssociatedPartyName: associatedpartyname,
        Entity_SellerName: sellerName,
        Entity_Brand: brand,
        Entity_Ranges: ranges,
        Entity_Versions: versions,
        Entity_PrixVehiculeHt: prixvehiculeht,
        Entity_MargeVehiculeHt: margevehiculeht,
        Entity_PrixOptionsHt: prixoptionsht,
        Entity_PrixAchatOptionsHt: prixachatoptionsht,
        Entity_MargeOptionsHt: margeoptionsht,
        Entity_PrixAccessoiresHt: prixaccessoiresht,
    };
}

function joinOrderContextParameters(rows: any[]): Record<(typeof ORDER_ENTITY_CONTEXT_KEYS)[number], string | null> {
    const parts = rows.map((r) => buildOrderContextParameters(r));
    const joinKey = (key: (typeof ORDER_ENTITY_CONTEXT_KEYS)[number]) => {
        const vals = parts.map((p) => p[key]).filter((v): v is string => v !== null && v !== undefined && `${v}`.trim() !== "");
        return vals.length ? vals.join(",") : null;
    };
    return {
        Entity_AssociatedPartyName: joinKey("Entity_AssociatedPartyName"),
        Entity_SellerName: joinKey("Entity_SellerName"),
        Entity_Brand: joinKey("Entity_Brand"),
        Entity_Ranges: joinKey("Entity_Ranges"),
        Entity_Versions: joinKey("Entity_Versions"),
        Entity_PrixVehiculeHt: joinKey("Entity_PrixVehiculeHt"),
        Entity_MargeVehiculeHt: joinKey("Entity_MargeVehiculeHt"),
        Entity_PrixOptionsHt: joinKey("Entity_PrixOptionsHt"),
        Entity_PrixAchatOptionsHt: joinKey("Entity_PrixAchatOptionsHt"),
        Entity_MargeOptionsHt: joinKey("Entity_MargeOptionsHt"),
        Entity_PrixAccessoiresHt: joinKey("Entity_PrixAccessoiresHt"),
    };
}

function extractOrderContextFromMessage(msg: any): Record<(typeof ORDER_ENTITY_CONTEXT_KEYS)[number], string | null> {
    const out = {} as Record<(typeof ORDER_ENTITY_CONTEXT_KEYS)[number], string | null>;
    for (const key of ORDER_ENTITY_CONTEXT_KEYS) {
        out[key] = paramStr(messageFieldValue(msg, key));
    }
    return out;
}

function mapOrderModuleStateToContextRow(): any | null {
    const orders0 = store.state.orderModule?.orders?.[0];
    if (orders0 && (pickOrderRowField(orders0, "associatedpartyname") || pickOrderRowField(orders0, "referenceorder", "referenceOrder"))) {
        return orders0;
    }
    const order = store.state.orderModule?.order;
    if (!order?.reference) return null;
    const orderItem = order.orderItems?.[0];
    const asset = orderItem?.orderAssets?.find((a: any) => !a?.assetTradeIn) || orderItem?.orderAssets?.[0];
    const pickLabel = (v: any) => {
        if (v === undefined || v === null) return "";
        if (typeof v === "object") return String(v.label ?? v.value ?? "").trim();
        return String(v).trim();
    };
    let associatedpartyname = "";
    for (const op of order.orderParties || []) {
        const role = op?.orderParty?.role?.resourceUid;
        if (role !== "CUSTOMER") continue;
        const rd = op?.orderParty?.party?.rawData?.responseData;
        if (rd?.name) associatedpartyname = String(rd.name).trim();
        else if (rd?.firstName || rd?.familyName) associatedpartyname = `${rd.firstName || ""} ${rd.familyName || ""}`.trim();
        break;
    }
    const items: any[] = asset?.proposalAssetItems || [];
    const itemType = (item: any) => String(item?.objectType || item?.featureType || item?.type?.resourceUid || "").toUpperCase();
    const sumItemsByType = (typePart: string, amountKey: "amountWoTax" | "purchaseAmountWoTax" | "optionMargin") => {
        const matched = items.filter((i) => itemType(i).includes(typePart));
        if (!matched.length) return null;
        return matched.reduce((s, i) => s + (Number(i?.[amountKey]?.amount) || 0), 0);
    };
    const orderResourceUid = String(order.resourceUid || pickOrderRowField(orders0, "orderid", "orderId", "resourceUid") || "").trim();
    return {
        referenceorder: order.reference,
        resourceUid: orderResourceUid,
        orderid: pickOrderRowField(orders0, "orderid", "orderId") || orderResourceUid,
        externalreference: orderItem?.externalReference || pickOrderRowField(orders0, "externalReference", "externalreference"),
        associatedpartyname,
        seller: order.assignedSalesPerson || "",
        brand: pickLabel(asset?.brand),
        ranges: pickLabel(asset?.range),
        versions: pickLabel(asset?.version),
        prixvehiculeht: asset?.amountWoTax?.amount,
        margevehiculeht: asset?.assetMargin?.amount,
        prixoptionsht: sumItemsByType("OPTION", "amountWoTax"),
        prixachatoptionsht: sumItemsByType("OPTION", "purchaseAmountWoTax"),
        margeoptionsht: sumItemsByType("OPTION", "optionMargin"),
        prixaccessoiresht: sumItemsByType("ACCESSORY", "amountWoTax"),
    };
}

function orderPartyRoleLabel(role: string | undefined): string {
    if (!role) return "";
    const key = `demand.party.roles.${role}`;
    const translated = i18n.global.t(key);
    return translated === key ? role : translated;
}
function normalizeRecipientEmail(e: string | undefined | null): string {
    return String(e ?? "").trim().toLowerCase();
}
function normalizeResourceKey(s: string | undefined | null): string {
    return String(s ?? "").trim().toLowerCase();
}
function findUserMatchingPartyEntry(fromStore: any[], partyEmailEntry: any): any | null {
    const userOt = "odm.user.leaseforgeuser";
    // l'email identifie la personne : on l'utilise en premier, sinon le destinataire enregistré dans
    // l'historique peut être un autre utilisateur rattaché au même tiers que celui qui reçoit le mail
    const em = normalizeRecipientEmail(partyEmailEntry.email);
    if (em) {
        const byEmail = fromStore.find(
            (x: any) => x.objectType === userOt && normalizeRecipientEmail(x.email) === em
        );
        if (byEmail) return { ...byEmail };
    }
    const thirdKey = normalizeResourceKey(partyEmailEntry.thirdResourceUid);
    if (thirdKey) {
        // repli sur le tiers uniquement s'il ne désigne qu'un seul utilisateur : sinon l'association est ambiguë
        const byThird = fromStore.filter(
            (x: any) => x.objectType === userOt && normalizeResourceKey(x.thirdResourceUid) === thirdKey
        );
        if (byThird.length === 1) return { ...byThird[0] };
    }
    return null;
}
function recipientSuggestionKey(r: any): string {
    if (r.isIntervenantChoiceRow) {
        return `iv:${normalizeResourceKey(r.thirdResourceUid)}:${normalizeRecipientEmail(r.emailOption?.email)}`;
    }
    if (r.isOrderPartyLinkedUser) return `lu:${r.resourceUid ?? ""}`;
    if (r.isFromOrderParty && r.isEmail) return `pe:${normalizeRecipientEmail(r.email)}`;
    return `x:${r.objectType ?? ""}:${r.resourceUid ?? r.value ?? ""}`;
}
function getRecipientTypeLabel(r: any): string {
    if (r.isOrderPartyLinkedUser) return "Utilisateur";
    if (r.isFromOrderParty && r.isEmail) return orderPartyRoleLabel(r.orderPartyRole) || "Email";
    if (r.isParty && r.role) return orderPartyRoleLabel(r.role);
    if (r.isParty) return "Tiers";
    if (r.isPhone || r.objectType === "phone") return "Téléphone";
    if (r.objectType === "odm.user.leaseforgeuser") return "Utilisateur";
    if (r.objectType === "odm.user.securitygroup") return "Groupe";
    if (r.isEmail || r.objectType === "email") return "Email";
    return "Dest.";
}
function getRecipientTypeClass(r: any): string {
    if (r.isFromOrderParty && r.orderPartyRole && (r.isEmail || r.objectType === "email")) {
        return "recip-badge-order-party";
    }
    if (r.isOrderPartyLinkedUser) return "recip-badge-user";
    if (r.isParty) return "recip-badge-group";
    if (r.isPhone || r.objectType === "phone") return "recip-badge-user";
    if (r.objectType === "odm.user.leaseforgeuser") return "recip-badge-user";
    if (r.objectType === "odm.user.securitygroup") return "recip-badge-group";
    if (r.isEmail) return "recip-badge-email";
    return "recip-badge-default";
}
function getRecipientDisplayName(r: any): string {
    if (r.isPhone) return r.phone;
    if (r.isFromOrderParty && r.name) return r.name;
    if (r.isParty) return r.name || r.label || r.resourceUid || "Tiers";
    if (r.isEmail) return r.email;
    if (r.objectType === "odm.user.securitygroup") return r.resourceUid || r.label || r.name || "Groupe";
    if (r.firstName || r.lastName) return `${r.firstName || ''} ${r.lastName || ''}`.trim();
    return r.label || r.name || r.resourceUid || "Inconnu";
}
function getPrimaryRecipientPhone(r: any): string {
    const phones = getRecipientPhones(r);
    return phones[0] || "";
}
function recipientPhoneHint(r: any): string {
    if (r.isPhone) return r.phone || "";
    const phone = getPrimaryRecipientPhone(r);
    return phone || "Aucun moyen de communication téléphone";
}
function isValidEmail(email: string): boolean { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email); }
function isValidPhone(phone: string): boolean { return /^[+\d][\d\s().-]{5,}$/.test(phone.trim()); }
function normalizePhone(phone: string): string { return phone.replaceAll(/[^\d+]/g, "").trim(); }
function getRecipientPhones(r: any): string[] {
    if (Array.isArray(r?.phones) && r.phones.length > 0 && typeof r.phones[0] === "string") {
        const normalized = r.phones
            .filter((p: string) => isValidPhone(p))
            .map((p: string) => normalizePhone(p));
        return Array.from(new Set<string>(normalized));
    }
    const rawPhones = [
        r?.phone,
        r?.mobilePhone,
        r?.mobile_phone,
        r?.phoneNumber,
        r?.phone_number,
        r?.gsm,
        r?.cellPhone,
        r?.telephone,
        r?.tel,
        ...(Array.isArray(r?.phones) ? r.phones : []),
        ...(Array.isArray(r?.phoneNumbers) ? r.phoneNumbers : []),
        ...(Array.isArray(r?.contactMeans) ? r.contactMeans : []),
    ];
    return [...new Set(rawPhones
        .filter(Boolean)
        .map((p: any) => typeof p === "string" ? p : p?.value || p?.phone || p?.number || p?.phoneNumber)
        .filter((p: any) => typeof p === "string" && isValidPhone(p))
        .map((p: string) => normalizePhone(p)))];
}
function fileToEmailAttachment(file: File): Promise<any> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = (reader.result as string).split(",")[1];
            resolve({ label: file.name, originalFileName: file.name, format: file.type, content: base64 });
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}
function alertCssClass(id: string | undefined | null): string {
    const map: Record<string, string> = { retard: "al-retard", paiement: "al-paiement", doc: "al-doc", livraison: "al-liv", relance: "al-relance" };
    if (id == null || String(id).trim() === "") return "al-retard";
    const lower = String(id).toLowerCase();
    const key = Object.keys(map).find((k) => lower.includes(k));
    return key ? map[key] : "al-retard";
}
function resolveLinkToOrder(results: any) {
    const routeData = router.resolve(`${"/order/edit/"}${results[0].orderid}`);
    window.open(routeData.href, "_blank");
}
function isCurrentUserWithUser(msg: any, user: any): boolean {
    const uid = user?.userId || "";
    const firstName = user?.firstName || "";
    const lastName = user?.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();
    const senderUid =
        msg?.senderUid?.resourceUid ||
        msg?.senderUid ||
        msg?.senderuid?.resourceUid ||
        msg?.senderuid ||
        "";
    return senderUid === uid || msg.sender === fullName || msg._isMe === true;
}
function getEntityReferenceFromMessage(msg: any): any {
    const metadata = Array.isArray(msg?.metadata) ? msg.metadata : [];
    const fromMetadata = metadata.find((x: any) => (x?.key || "").toLowerCase() === "entity_reference")?.value;
    if (fromMetadata !== undefined) return fromMetadata;
    if (msg?.parameters?.Entity_Reference !== undefined) return msg.parameters.Entity_Reference;
    if (msg?.parameters?.entity_reference !== undefined) return msg.parameters.entity_reference;
    return undefined;
}
function senderInitials(msg: any): string {
    const name = msg.sender || "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
}
function bubbleClassForUser(msg: any, user: any): string {
    return isCurrentUserWithUser(msg, user) ? "bubble-me" : "bubble-them";
}
function flattenOrderDocuments(orderDocuments: any[]): any[] {
    const flattened: any[] = [];
    orderDocuments.forEach((gatheringProcess: any) => {
        const processLabel = gatheringProcess.assessmentContext || "Document";
        const checkpoints = gatheringProcess.checkpoints;
        if (!Array.isArray(checkpoints)) return;
        checkpoints.forEach((checkpoint: any) => {
            const documents = checkpoint.documents;
            if (!Array.isArray(documents)) return;
            documents.forEach((doc: any) => {
                const upload = doc.documentUpload;
                if (!upload?.resourceUid || !upload?.originalFileName) return;
                flattened.push({
                    resourceUid: upload.resourceUid,
                    displayName: upload.originalFileName,
                    processLabel,
                    documentType: doc.documentType,
                    status: doc.status,
                });
            });
        });
    });
    return flattened;
}

export default defineComponent({
    name: "OrderMessagesPanel",

    props: {
        modelValue: { type: Boolean, default: false },
        /** Rend le panneau dans le flux (onglet) plutot qu'en tiroir lateral. */
        inline: { type: Boolean, default: false },
        orderReference: { type: String, default: "" },
        orderExternalReference: { type: String, default: "" },
        orderLabel: { type: String, default: "" },
        orderDocuments: { type: Array as PropType<any[]>, default: () => [] },
        subjectsList: { type: Array as PropType<any[]>, default: () => [] },
        referenceOptions: { type: Array as PropType<any[]>, default: () => [] },
        /** Orders selected in the order list, used to prefill compose references. */
        selectedOrders: { type: Array as PropType<any[]>, default: () => [] },
        alertList: { type: Array as PropType<any[]>, default: () => [] },
        orderId: { type: String, default: "" },
    },

    emits: ["update:modelValue", "message-sent", "update:unreadCount", "update:totalMessagesCount"],

    setup(props, { emit }) {

        const MESSAGE_TYPE_REF = MESSAGE_TYPE;

        // ── UI state ──
        const isOpen = ref(props.modelValue);
        const activeTab = ref<"all" | "alerts" | "msgs" | "memo" | "emails">("all");
        const readFilter = ref<"all" | "unread" | "read" | "treated" | "deleted">("all");
        const searchInput = ref("");
        const sortBy = ref<"date" | "priority">("date");
        const sortDirection = ref<"asc" | "desc">("desc");
        const deletingAll = ref(false);
        const selectedMessage = ref<any>(null);
        const showCompose = ref(false);
        const markingRead = ref(false);
        const markingTreated = ref(false);
        const sendingMessage = ref(false);
        const composeError = ref("");

        // Reference search state
        const refSearch = ref("");
        const refDropOpen = ref(false);
        const refFilteredOptions = ref<any[]>([]);
        const refLoading = ref(false);
        let refSearchTimer: ReturnType<typeof setTimeout> | null = null;

        // Recipient search state
        const recipientSearch = ref("");
        const recipDropOpen = ref(false);
        const recipientFilter = ref<"All" | "User" | "Group">("All");

        // Doc search
        const docSearch = ref("");

        // ── Static config ──
        const tabs: Array<{ key: "all" | "alerts" | "msgs" | "memo" | "emails"; icon: string; label: string }> = [
            { key: "all", icon: "", label: "Tous" },
            { key: "alerts", icon: "⚡ ", label: "Alertes" },
            { key: "msgs", icon: "📨 ", label: "Messages" },
            { key: "memo", icon: "📝 ", label: "Mémos" },
            { key: "emails", icon: "📧 ", label: "Emails" },
        ];
        const readFilters: Array<{ key: "all" | "unread" | "read" | "treated" | "deleted"; label: string }> = [
            { key: "all", label: "Tous" },
            { key: "unread", label: "Non lus" },
            { key: "read", label: "Lus" },
            { key: "treated", label: "Traités" },
            { key: "deleted", label: "Supprimés" },
        ];
        /**
         * Seules les alertes sont réellement supprimables (clôturées) : un message ne peut pas être
         * supprimé côté odm-contactmechanism, et un email n'est pas supprimable non plus. On ne
         * propose donc « Supprimés » que là où la liste peut en contenir.
         */
        const TABS_WITH_DELETIONS = ["all", "alerts"];
        const visibleReadFilters = computed(() =>
            readFilters.filter((f) => f.key !== "deleted" || TABS_WITH_DELETIONS.includes(activeTab.value))
        );
        // en changeant d'onglet, ne pas rester sur un filtre qui n'y est plus proposé
        watch(activeTab, (tab) => {
            if (readFilter.value === "deleted" && !TABS_WITH_DELETIONS.includes(tab)) readFilter.value = "all";
        });
        const msgTypes = [
            { key: MESSAGE_TYPE.MEMO, icon: "📝", label: "Mémo" },
            { key: MESSAGE_TYPE.MESSAGE, icon: "📨", label: "Message" },
            { key: MESSAGE_TYPE.SMS, icon: "💬", label: "SMS" },
            { key: MESSAGE_TYPE.ALERT, icon: "⚡", label: "Alerte" },
        ];
        const visibleMsgTypes = computed(() =>
            msgTypes.filter((t) => t.key !== MESSAGE_TYPE.MEMO)
        );
        const alertTypes = ref<{ key: string; label: string; cssClass: string }[]>([]);

        // ── Compose factory ──
        function makeCompose() {
            return {
                msgType: MESSAGE_TYPE.MESSAGE as MESSAGE_TYPE,
                sendDate: new Date().toISOString().slice(0, 10),
                alertType: [] as string[],
                important: false,
                subject: "" as string,
                selectedRefs: [] as any[],
                selectedRecipients: [] as any[],
                body: "",
                selectedDocs: [] as string[],
                attachedFile: null as File | null,
                replyTo: null as any,
                transferOf: null as any,
            };
        }
        const compose = ref(makeCompose());

        // ── Store accessors ──
        const allNotifications = computed<any[]>(
            () => (store.getters["notificationModule/getData"] as any[]) || []
        );
        const storeRecipients = computed<any[]>(
            () => (store.state as any).messagingModule?.recipients || []
        );
        const smsPartyRecipients = ref<any[]>([]);
        /** Emails issus de orderModule.order.orderParties (intervenants), pour suggestions en mode message / alerte. */
        const emailPartyRecipients = ref<any[]>([]);

        /**
         * Alertes de la commande, supprimées comprises : une alerte supprimée est en réalité clôturée
         * (alertValidity.until renseigné) et doit rester consultable via le filtre « Supprimés ».
         * La source order.orderAlerts est privilégiée car, contrairement à la liste issue de la query,
         * elle porte businessData (deletedBy / deletedAt).
         */
        const orderAlerts = computed<any[]>(() => {
            const fromOrder = store.state.orderModule?.order?.orderAlerts;
            if (Array.isArray(fromOrder) && fromOrder.length > 0 && orderMatchesPanelContext(store.state.orderModule?.order)) {
                return fromOrder.map((a: any) => {
                    const reasons = (a.reasons || []).map((r: any) => r?.reason?.resourceUid).filter((r: any) => r);
                    return mapAlertToMessageCard({
                        id: a.resourceUid,
                        createdBy: a.createdBy,
                        createdAt: a.createdAt,
                        comment: a.alertComment,
                        reasons: reasons.join(","),
                        alertValidity: a.alertValidity,
                        orderId: store.state.orderModule?.order?.resourceUid,
                        orderReference: props.orderReference,
                        businessData: a.businessData,
                    });
                });
            }
            const list = props.alertList ? (props.alertList || []) : store.state.alertList || [];
            return list.filter((a: any) => a && a.alertValidity).map((a: any) => mapAlertToMessageCard(a));
        });

        /**
         * Destinataire affiché dans la liste (« À : ») : nom et prénom pour un utilisateur,
         * intitulé du rôle pour un groupe, adresse pour un email. Vide pour une alerte de commande,
         * qui n'a pas de destinataire.
         */
        function recipientLabel(msg: any): string {
            // alerte : pas de destinataire propre, on affiche ceux mémorisés à la création s'il y en a
            if (msg?._isAlert) return msg._recipients || "";
            if (msg?._isEmail) return msg.recipient || "";
            const recipients = Array.isArray(msg?.recipients) ? msg.recipients : [];
            if (recipients.length > 0) {
                const names = recipients
                    .map((entry: any) => {
                        const r = entry?.recipient || entry;
                        if (!r) return "";
                        if (String(r.objectType || "").includes("securitygroup")) {
                            // translateGroupName renvoie la clé i18n quand le libellé n'existe pas
                            const label = String(translateGroupName(r.resourceUid) || "");
                            return label.includes("middleOffice") ? r.resourceUid || "" : label;
                        }
                        const known = (storeRecipients.value || []).find(
                            (x: any) => String(x.resourceUid) === String(r.resourceUid)
                        );
                        return known ? getRecipientDisplayName(known) : r.resourceUid || "";
                    })
                    .filter((n: string) => n);
                if (names.length > 0) return names.join(", ");
            }
            // envoi par email : aucun destinataire en base, on retombe sur la trace posée à l'envoi
            return msg?.recipient || messageMetadataValue(msg, "Recipients") || msg?.parameters?.Recipients || channelText(msg) || "";
        }

        const historicMessages = computed<any[]>(
            () => (store.getters["messagingModule/getHistoricMessages"] as any[]) || []
        );
        const loadingThread = ref(false);

        // ── Messages scoped to this order ──
        const orderMessages = computed<any[]>(() => {
            const ref = props.orderReference;
            const excludedNotification = allNotifications.value.filter((m: any) => !!m.sender);

            // En mode inline le panneau occupe un onglet de la commande : il ne doit
            // montrer QUE le contexte de cette commande. Tant que sa reference n'est
            // pas connue, il n'y a rien a montrer - afficher la boite globale en
            // attendant donnerait a croire que ces messages portent sur le dossier.
            if (!ref) return props.inline ? [] : excludedNotification;

            return excludedNotification.filter((m: any) => {
                const entityRefRaw = getEntityReferenceFromMessage(m);
                const entityRef = typeof entityRefRaw === "string" ? entityRefRaw.trim() : entityRefRaw;
                if (entityRef === ref) return true;

                // Un message sans reference explicite reste visible dans le tiroir
                // lateral, qui est la boite de reception de l'utilisateur. L'onglet
                // d'une commande, lui, exige une correspondance stricte : ces messages
                // ne portent sur aucun dossier, ils n'ont pas a s'y afficher.
                if (props.inline) return false;

                const hasNoReference = entityRef === null || entityRef === undefined || entityRef === "";
                if (!hasNoReference) return false;
                const kind = m?.type?.resourceUid;
                return !kind || kind === MESSAGE_TYPE.MESSAGE || kind === MESSAGE_TYPE.SMS || kind === MESSAGE_TYPE.MEMO;
            });
        });

        // ── Emails (utilisateur connecté = expéditeur ou destinataire) ──
        const selectedEmail = ref<any>(null);

        /** _business_data est renvoyé par le dataservice sous forme de chaîne JSON (colonne jsonb → OTHER). */
        function parseBusinessData(raw: any): Record<string, any> {
            if (!raw) return {};
            if (typeof raw === "object") return raw;
            try { return JSON.parse(raw); } catch { return {}; }
        }

        const storeOrderUserEmails = computed<any[]>(
            () => (store.getters["messagingModule/getOrderUserEmails"] as any[]) || []
        );

        /**
         * Emails normalisés au même modèle que les messages (pour réutiliser la carte),
         * scopés à la commande courante via _business_data (Entity_Reference ou contract_reference),
         * car la query ne peut pas filtrer sur ces clés JSONB en casse mixte.
         */
        const orderEmails = computed<any[]>(() => {
            const ref = props.orderReference ? String(props.orderReference).trim() : "";
            return storeOrderUserEmails.value
                .map((row: any) => {
                    const bd = parseBusinessData(row.businessdata);
                    const reference = bd.Entity_Reference ?? bd.contract_reference ?? "";
                    const externalReference = bd.Entity_ExternalReference ?? "";
                    const date = row.sentdate || row.createddate;
                    return {
                        resourceUid: row.resourceuid,
                        sender: bd.sender || row.sender || "",       // sender depuis _business_data.sender en priorité
                        recipient: row.recipient || "",
                        copyRecipient: row.copyrecipient || "",
                        subject: row.subject || "—",
                        body: row.body || "",
                        content: row.body || "",
                        communicationDate: date,
                        type: { objectType: "odm.contactmechanism.emailevent", resourceUid: "EMAIL" },
                        status: null,
                        priority: 0,
                        channel: row.recipient || "",
                        metadata: [
                            { key: "Entity_Reference", value: reference },
                            { key: "Entity_ExternalReference", value: externalReference },
                        ],
                        _isEmail: true,
                        _reference: reference,
                        _externalReference: externalReference,
                        _businessData: bd,
                    };
                })
                // Rattachement à la commande : si aucune référence n'est fournie au panneau, tout afficher.
                .filter((e: any) => !ref || String(e._reference).trim() === ref);
        });

        function openEmail(email: any) { selectedEmail.value = email; }
        function closeEmail() { selectedEmail.value = null; }
        /** Détermine si le corps de l'email est du HTML (aligné sur EmailMechanismServiceImpl.isHtml côté backend). */
        function emailIsHtml(body: string): boolean {
            if (!body) return false;
            const t = body.trim().toLowerCase();
            return t.startsWith("<!doctype") || t.startsWith("<html") ||
                /<\s*(html|body|div|span|p|br|h[1-6]|table|tr|td|strong|em|a)[^>]*>/.test(t);
        }

        // ── Flattened documents ──
        const flattenedDocuments = computed<any[]>(() => {
            if (!props.orderDocuments || !Array.isArray(props.orderDocuments)) return [];
            return flattenOrderDocuments(props.orderDocuments);
        });

        // ── Filtered documents (search) ──
        const filteredFlattenedDocuments = computed(() => {
            const q = docSearch.value.trim().toLowerCase();
            if (!q) return flattenedDocuments.value;
            return flattenedDocuments.value.filter((doc: any) =>
                (doc.displayName || "").toLowerCase().includes(q) ||
                (doc.processLabel || "").toLowerCase().includes(q)
            );
        });

        function toggleDocItem(uid: string) {
            const idx = compose.value.selectedDocs.indexOf(uid);
            if (idx >= 0) compose.value.selectedDocs.splice(idx, 1);
            else compose.value.selectedDocs.push(uid);
        }

        // ── Parent messages ──
        const parentMessages = computed<any[]>(() => {
            if (!historicMessages.value || historicMessages.value.length === 0) return [];
            return historicMessages.value.map((pack: any) => {
                const messagePack = pack.messagePack || [];
                if (messagePack.length === 0) return null;
                const parentMsg = messagePack.find((m: any) => !m.replymsgparent && !m.transfermsgparent) || messagePack[0];
                const subjectObj = typeof parentMsg.subject === 'object' && parentMsg.subject?.objectType
                    ? parentMsg.subject
                    : { objectType: "odm.contactmechanism.messagesubject", systemUid: "odm-contactmechanism", resourceUid: parentMsg.subject };
                return {
                    resourceUid: parentMsg.resourceuid || parentMsg.resourceUid,
                    sender: parentMsg.sender,
                    senderUid: parentMsg.senderuid ? { objectType: "odm.user.leaseforgeuser", systemUid: "odm-user", resourceUid: parentMsg.senderuid } : null,
                    body: parentMsg.body,
                    content: parentMsg.body,
                    communicationDate: parentMsg.date,
                    subject: subjectObj,
                    status: { resourceUid: parentMsg.status },
                    type: { objectType: "odm.contactmechanism.messagetype", systemUid: "odm-contactmechanism", resourceUid: parentMsg.type || "MESSAGE" },
                    priority: parentMsg.priority,
                    metadata: parentMsg.metadata,
                    recipient: parentMsg.recipientname || parentMsg.recipient_resourceuid,
                    _replyCount: messagePack.length - 1,
                    _messagePack: messagePack,
                    _isFromHistoric: true,
                };
            })
                .filter(Boolean)
                .filter((m: any) => !!m.sender)
                .sort((a: any, b: any) => {
                    const da = a.communicationDate ? new Date(a.communicationDate).getTime() : 0;
                    const db = b.communicationDate ? new Date(b.communicationDate).getTime() : 0;
                    return db - da;
                });
        });

        const historicParentByMsgUid = computed<Record<string, string>>(() => {
            const map: Record<string, string> = {};
            for (const pack of historicMessages.value || []) {
                const messagePack = pack?.messagePack || [];
                if (!Array.isArray(messagePack) || messagePack.length === 0) continue;
                const parent = messagePack.find((m: any) => !m.replymsgparent && !m.transfermsgparent) || messagePack[0];
                const parentUid = parent?.resourceuid || parent?.resourceUid;
                if (!parentUid) continue;
                for (const m of messagePack) {
                    const uid = m?.resourceuid || m?.resourceUid;
                    if (uid) map[uid] = parentUid;
                }
            }
            return map;
        });

        type ThreadGroup = { key: string; root: any; messages: any[]; lastDateMs: number; unreadCount: number; };
        function threadKeyForMessage(msg: any): string {
            const explicit = explicitMsgRootFromMessage(msg);
            if (explicit) return explicit;
            const uid = msg?.resourceUid || msg?.resourceuid || "";
            const historicParent = uid ? historicParentByMsgUid.value[uid] : undefined;
            if (historicParent) return historicParent;
            return uid || "";
        }

        const threadGroups = computed<Record<string, ThreadGroup>>(() => {
            const groups: Record<string, ThreadGroup> = {};
            for (const m of orderMessages.value || []) {
                const uid = m?.resourceUid || m?.resourceuid;
                if (!uid) continue;
                const key = threadKeyForMessage(m);
                if (!key) continue;
                if (!groups[key]) {
                    groups[key] = { key, root: null, messages: [], lastDateMs: 0, unreadCount: 0 };
                }
                groups[key].messages.push(m);
            }

            for (const key of Object.keys(groups)) {
                const g = groups[key];
                g.messages.sort((a: any, b: any) => {
                    const da = a.communicationDate ? new Date(a.communicationDate).getTime() : 0;
                    const db = b.communicationDate ? new Date(b.communicationDate).getTime() : 0;
                    return da - db;
                });
                g.root =
                    g.messages.find((m: any) => (m?.resourceUid || m?.resourceuid) === key) ||
                    g.messages[0];
                g.lastDateMs = g.messages.reduce((acc: number, m: any) => {
                    const d = m.communicationDate ? new Date(m.communicationDate).getTime() : 0;
                    return Math.max(acc, d);
                }, 0);
                g.unreadCount = g.messages.filter((m: any) => !isRead(m) && !isAlert(m)).length;
            }
            return groups;
        });

        // Message non lu pris en compte par le compteur "Non lus" (hors alertes).
        function isUnreadCandidate(m: any): boolean {
            if (!m || m._isAlert || m._isEmail || isRead(m)) return false;
            if (m.type?.resourceUid === MESSAGE_TYPE.ALERT) return false;
            const alertType = m?.metadata?.find((meta: any) => meta.key === "Alert_Type")?.value;
            return !alertType || alertType === "null" || alertType.trim() === "";
        }

        function isConversationMessage(m: any): boolean {
            return m.type?.resourceUid === MESSAGE_TYPE.MESSAGE || m.type?.resourceUid === MESSAGE_TYPE.SMS || (!!m.channel && m.type?.resourceUid !== MESSAGE_TYPE.MEMO);
        }

        // Messages non lus d'un élément de la liste : toute la conversation pour un message/SMS racine.
        function unreadMessagesOf(m: any): any[] {
            if (!m || m._isAlert || m._isEmail) return [];
            if (m.type?.resourceUid !== MESSAGE_TYPE.MEMO && isConversationMessage(m)) {
                const g = threadGroups.value[threadKeyForMessage(m)];
                if (g) return g.messages.filter((mm: any) => isUnreadCandidate(mm));
            }
            return isUnreadCandidate(m) ? [m] : [];
        }

        function threadHasUnread(m: any): boolean { return unreadMessagesOf(m).length > 0; }

        function isThreadRoot(msg: any): boolean {
            const uid = msg?.resourceUid || msg?.resourceuid;
            if (!uid) return false;
            const key = threadKeyForMessage(msg);
            const g = threadGroups.value[key];
            if (!g?.root) return true;
            return (g.root?.resourceUid || g.root?.resourceuid) === uid;
        }

        function matchesThreadQuery(msg: any, q: string): boolean {
            if (!q) return true;
            const key = threadKeyForMessage(msg);
            const g = threadGroups.value[key];
            if (!g) return false;
            return g.messages.some((mm: any) =>
                subjectText(mm).toLowerCase().includes(q) ||
                (mm.body || mm.content || "").toLowerCase().includes(q) ||
                (mm.sender || "").toLowerCase().includes(q)
            );
        }

        function sortDateForListItem(m: any): number {
            const direct = m?.communicationDate ? new Date(m.communicationDate).getTime() : 0;
            if (m?._isAlert || m.type?.resourceUid === MESSAGE_TYPE.MEMO) return direct;
            const key = threadKeyForMessage(m);
            return threadGroups.value[key]?.lastDateMs || direct;
        }

        // ── Filtered messages ──
        const filteredMessages = computed<any[]>(() => {
            // Onglet Emails : liste dédiée, indépendante du modèle de conversation et des filtres lu/traité.
            if (activeTab.value === "emails") {
                const q = searchInput.value.trim().toLowerCase();
                let emails = orderEmails.value;
                if (q) {
                    emails = emails.filter((e: any) =>
                        subjectText(e).toLowerCase().includes(q) ||
                        (e.body || "").toLowerCase().includes(q) ||
                        (e.sender || "").toLowerCase().includes(q) ||
                        (e.recipient || "").toLowerCase().includes(q)
                    );
                }
                const dir = sortDirection.value === "desc" ? -1 : 1;
                return [...emails].sort((a, b) => {
                    const da = a.communicationDate ? new Date(a.communicationDate).getTime() : 0;
                    const db = b.communicationDate ? new Date(b.communicationDate).getTime() : 0;
                    return (da - db) * dir;
                });
            }

            const msgs = [...orderMessages.value];
            const alerts = orderAlerts.value;
            let list: any[];

            if (activeTab.value === "alerts") {
                list = [...alerts];
            } else if (activeTab.value === "msgs") {
                list = msgs.filter((m) => {
                    const isMsg = m.type?.resourceUid === MESSAGE_TYPE.MESSAGE || m.type?.resourceUid === MESSAGE_TYPE.SMS || (m.channel && m.type?.resourceUid !== MESSAGE_TYPE.MEMO);
                    if (!isMsg) return false;
                    const alertType = m?.metadata?.find((meta: any) => meta.key === "Alert_Type")?.value;
                    return !alertType || alertType === "null" || alertType.trim() === "";
                });
            } else if (activeTab.value === "memo") {
                list = msgs.filter((m) => m.type?.resourceUid === MESSAGE_TYPE.MEMO);
            } else {
                const msgsWithoutAlertType = msgs.filter((m) => {
                    const alertType = m?.metadata?.find((meta: any) => meta.key === "Alert_Type")?.value;
                    return !alertType || alertType === "null" || alertType.trim() === "";
                });
                list = [...msgsWithoutAlertType, ...alerts];
            }

            // Messenger-style: for MESSAGE/SMS keep only root items in the list
            list = list.filter((m: any) => {
                if (m?._isAlert) return true;
                if (m.type?.resourceUid === MESSAGE_TYPE.MEMO) return true;
                const isMsg = m.type?.resourceUid === MESSAGE_TYPE.MESSAGE || m.type?.resourceUid === MESSAGE_TYPE.SMS || (m.channel && m.type?.resourceUid !== MESSAGE_TYPE.MEMO);
                if (!isMsg) return true;
                return isThreadRoot(m);
            });

            // « Supprimés » isole les éléments supprimés ; les autres vues les excluent.
            if (readFilter.value === "deleted") list = list.filter((m) => isDeletedItem(m));
            else list = list.filter((m) => !isDeletedItem(m));

            // EFLO-915 : la liste ne garde que les racines de conversation, alors que le compteur "Non lus"
            // compte aussi les réponses. On filtre donc au niveau de la conversation pour rester cohérent.
            if (readFilter.value === "unread") list = list.filter((m) => threadHasUnread(m));
            else if (readFilter.value === "read") list = list.filter((m) => READ_STATES.has(m?.status?.resourceUid || m?.status || "") && !threadHasUnread(m));
            else if (readFilter.value === "treated") list = list.filter((m) => TREATED_STATES.has(m?.status?.resourceUid || m?.status || ""));

            const q = searchInput.value.trim().toLowerCase();
            if (q) {
                list = list.filter((m) =>
                    subjectText(m).toLowerCase().includes(q) ||
                    (m.body || m.content || "").toLowerCase().includes(q) ||
                    (m.sender || "").toLowerCase().includes(q) ||
                    matchesThreadQuery(m, q)
                );
            }
            const direction = sortDirection.value === "desc" ? -1 : 1;
            return list.sort((a, b) => {
                if (sortBy.value === "priority") {
                    const pa = getPriorityValue(a);
                    const pb = getPriorityValue(b);
                    if (pa !== pb) return (pa - pb) * direction;
                }
                const da = sortDateForListItem(a);
                const db = sortDateForListItem(b);
                return (da - db) * direction;
            });
        });

        const unreadCount = computed(() => orderMessages.value.filter((m) => isUnreadCandidate(m)).length);

        function tabCount(key: string): number {
            const list = orderMessages.value;
            // les éléments supprimés sont exclus des vues Tous / Non lus / Lus / Traités
            const alertCount = orderAlerts.value.filter((a: any) => !isDeletedItem(a)).length;
            const hasAlertType = (m: any) => {
                const val = m?.metadata?.find((meta: any) => meta.key === "Alert_Type")?.value;
                return val && val !== "null" && val.trim() !== "";
            };
            if (key === "all") { const cleanMsgs = list.filter((m) => !hasAlertType(m)); return cleanMsgs.length + alertCount; }
            if (key === "alerts") { const notifAlerts = list.filter((m) => m.type?.resourceUid === MESSAGE_TYPE.ALERT || m.type?.resourceUid === MESSAGE_TYPE.WARN || (m.priority && m.priority >= 3)).length; return notifAlerts + alertCount; }
            if (key === "msgs") { return list.filter((m) => { const isMsg = m.type?.resourceUid === MESSAGE_TYPE.MESSAGE || m.type?.resourceUid === MESSAGE_TYPE.SMS || (m.channel && m.type?.resourceUid !== MESSAGE_TYPE.MEMO); return isMsg && !hasAlertType(m); }).length; }
            if (key === "memo") { return list.filter((m) => m.type?.resourceUid === MESSAGE_TYPE.MEMO).length; }
            if (key === "emails") { return orderEmails.value.length; }
            return 0;
        }

        // ── Text & display helpers ──

        function toggleSortDirection() {
            sortDirection.value = sortDirection.value === "desc" ? "asc" : "desc";
        }

        async function openOrderTab(orderReference?: any) {
            if (orderReference) {
                await store.dispatch("orderModule/searchOrder", {
                    callback: resolveLinkToOrder,
                    filter: { notificationFilter: orderReference },
                });
                return;
            }
            if (props.orderId) {
                const routeData = router.resolve(`/order/edit/${props.orderId}`);
                window.open(routeData.href, "_blank");
            }
        }

        // ── Status helpers ──

        // ── Type / style helpers ──
        function isAlert(m: any) { return m.type?.resourceUid === MESSAGE_TYPE.ALERT || (m.priority && m.priority >= 3); }
        function msgIcon(m: any): string {
            if (!m) return "📨";
            if (m._isEmail) return "📧";
            if (isAlert(m)) return "🚨";
            if (m.type?.resourceUid === MESSAGE_TYPE.WARN) return "⚠️";
            if (m.type?.resourceUid === MESSAGE_TYPE.MEMO) return "📝";
            if (m.type?.resourceUid === MESSAGE_TYPE.SMS) return "💬";
            return "📨";
        }
        function msgCardBorderClass(m: any): string {
            if (isAlert(m)) return "mc-alert";
            if (m.type?.resourceUid === MESSAGE_TYPE.WARN) return "mc-warn";
            return "";
        }
        function avatarColorClass(m: any): string {
            if (m._isEmail) return "mca-indigo";
            if (isAlert(m)) return "mca-red";
            if (m.type?.resourceUid === MESSAGE_TYPE.WARN) return "mca-orange";
            if (m.type?.resourceUid === MESSAGE_TYPE.MEMO) return "mca-teal";
            if (m.type?.resourceUid === MESSAGE_TYPE.SMS) return "mca-purple";
            return "mca-blue";
        }
        function typeLabel(m: any): string {
            if (m._isEmail) return "📧 Email";
            if (isAlert(m)) return "🚨 Alerte";
            if (m.type?.resourceUid === MESSAGE_TYPE.WARN) return "⚠️ Alerte";
            if (m.type?.resourceUid === MESSAGE_TYPE.MEMO) return "📝 Mémo";
            if (m.type?.resourceUid === MESSAGE_TYPE.SMS) return "💬 SMS";
            return "📨 Message";
        }
        function typeTagClass(m: any): string {
            if (m._isEmail) return "tg-email";
            if (isAlert(m)) return "tg-alert";
            if (m.type?.resourceUid === MESSAGE_TYPE.WARN) return "tg-warn";
            if (m.type?.resourceUid === MESSAGE_TYPE.MEMO) return "tg-memo";
            if (m.type?.resourceUid === MESSAGE_TYPE.SMS) return "tg-sms";
            return "tg-msg";
        }
        // ── Thread helpers ──
        const threadRef = ref<HTMLElement | null>(null);

        function bubbleAvatarClass(msg: any): string {
            if (isAlert(msg)) return "bav-red";
            if (msg.type?.resourceUid === MESSAGE_TYPE.MEMO) return "bav-teal";
            if (msg._threadType === 'transfer') return "bav-purple";
            if (msg._threadType === 'reply') return "bav-blue";
            return "bav-gray";
        }

        const isCurrentUser = (msg: any): boolean => isCurrentUserWithUser(msg, store.state.authModule?.user);
        const bubbleClass = (msg: any): string => bubbleClassForUser(msg, store.state.authModule?.user);

        function messageThread(rootMsg: any): any[] {
            if (!rootMsg) return [];
            const rootKey = threadKeyForMessage(rootMsg);
            const mergedByUid: Record<string, any> = {};

            appendThreadMessagesFromGroup(mergedByUid, threadGroups.value[rootKey]);

            // 2) Historic-side thread (usually contains complete A<->B exchanges)
            const msgPack = historicMessages.value.find((pack: any) => {
                const messagePack = pack?.messagePack || [];
                const parent = messagePack.find((m: any) => !m.replymsgparent && !m.transfermsgparent) || messagePack[0];
                const parentUid = parent?.resourceuid || parent?.resourceUid;
                return parentUid === rootKey;
            });
            appendThreadMessagesFromHistoricPack(mergedByUid, msgPack?.messagePack || []);
            const merged = sortThreadMessages(Object.values(mergedByUid));
            if (merged.length > 0) return merged;
            return [{ ...rootMsg, _threadType: "", _refSubject: "" }];
        }

        async function loadMessageThread(msg: any) {
            if (!msg || !props.orderReference) return;
            loadingThread.value = true;
            try {
                await store.dispatch("messagingModule/historicMessages", { type: "MESSAGE", entityId: props.orderReference });
            } catch (e) { console.error("loadMessageThread", e); }
            finally { loadingThread.value = false; }
        }

        // ── Reference search helpers ──

        function onRefSearch() {
            const val = refSearch.value.trim();
            refDropOpen.value = true;
            if (!val || val.length < 2) { refFilteredOptions.value = props.referenceOptions.slice(0, 20); return; }
            if (refSearchTimer) clearTimeout(refSearchTimer);
            refLoading.value = true;
            refSearchTimer = setTimeout(() => {
                store.dispatch("demandModule/searchOfferPartyOrder", {
                    callback: (res: any[]) => { refFilteredOptions.value = res || []; refLoading.value = false; },
                    offerFilter: { reference: `%${val}%` },
                    filterParty: { reference: `%${val}%` },
                    orderFilter: { externalReference: `%${val}%` },
                });
            }, 400);
        }

        function onRefInputFocus() {
            refDropOpen.value = true;
            if (!refSearch.value.trim() && refFilteredOptions.value.length === 0) { refFilteredOptions.value = props.referenceOptions.slice(0, 20); }
        }

        function onRecipInputFocus() { recipDropOpen.value = true; }

        function selectRef(opt: any) {
            const already = compose.value.selectedRefs.some((r) => refLabel(r) === refLabel(opt) && refContext(r) === refContext(opt));
            if (!already) {
                const resourceUid = resolveOrderResourceUid(opt, props.orderId, props.orderReference);
                compose.value.selectedRefs.push(resourceUid ? { ...opt, resourceUid } : opt);
            }
            refSearch.value = ""; refDropOpen.value = false; refFilteredOptions.value = [];
        }

        function getOrderExternalRef(): string {
            return props.orderExternalReference
                || store.state.orderModule?.orders?.[0]?.externalReference
                || store.state.orderModule?.order?.externalReference
                || "";
        }

        function buildOrderRefFallback(orderReference: string, externalRef: string): any {
            const orderState = store.state.orderModule?.order;
            const resourceUid = resolveOrderResourceUid(
                { referenceorder: orderReference },
                props.orderId || orderState?.resourceUid || "",
                orderReference,
            );
            if (externalRef) {
                return {
                    referenceorder: orderReference,
                    resourceUid,
                    externalreference: externalRef,
                    _daaq: orderState?._daaq || "",
                    status_code: orderState?.status?.resourceUid || "",
                };
            }
            return { referenceorder: orderReference, resourceUid, externalreference: "" };
        }

        function getSelectedOrderRefs(): any[] {
            const selectedOrders = props.selectedOrders.length > 0
                ? props.selectedOrders
                : ((store.state as any).orderModule?.selectedSearchOrderItem || []);

            return selectedOrders
                .filter((order: any) => order?.referenceorder || order?.reference || order?.externalReference || order?.externalreference)
                .map((order: any) => {
                    const reference = order.referenceorder || order.reference || "";
                    const externalRef = order.externalreference || order.externalReference || "";
                    const fromOptions = findOrderRefInOptions(
                        props.referenceOptions,
                        reference,
                        externalRef,
                        order.orderid || order.resourceUid,
                    );
                    return fromOptions || buildOrderRefFallback(reference, externalRef);
                });
        }

        function prefillSelectedOrderRefs(): boolean {
            const selectedOrderRefs = getSelectedOrderRefs();
            if (selectedOrderRefs.length === 0) return false;

            selectedOrderRefs.forEach((orderRef) => selectRef(orderRef));
            return true;
        }

        /** Pré-sélection commande : même objet que selectRef (referenceOptions puis searchOfferPartyOrder). */
        function prefillDefaultOrderRef() {
            if (compose.value.selectedRefs.length > 0) return;

            if (prefillSelectedOrderRefs()) return;

            if (!props.orderReference) return;

            const extRef = getOrderExternalRef();
            const fromOptions = findOrderRefInOptions(
                props.referenceOptions,
                props.orderReference,
                extRef,
                props.orderId,
            );
            if (fromOptions) {
                selectRef(fromOptions);
                return;
            }

            const orderFilter = extRef
                ? { externalReference: `%${extRef}%` }
                : { filterReference: props.orderReference };

            store.dispatch("demandModule/searchOfferPartyOrder", {
                callback: (res: any[]) => {
                    if (!showCompose.value || compose.value.selectedRefs.length > 0) return;
                    const match = pickOrderRefFromSearchResults(res, props.orderReference, extRef, props.orderId);
                    selectRef(match || buildOrderRefFallback(props.orderReference, extRef));
                },
                offerFilter: {},
                filterParty: {},
                orderFilter,
            });
        }

        function removeRef(idx: number) { compose.value.selectedRefs.splice(idx, 1); }

        function onDocClick(e: MouseEvent) { if (!(e.target as HTMLElement).closest(".ref-search-wrap")) { refDropOpen.value = false; } }

        // ── Compose computed ──
        const composeTitle = computed(() => {
            if (compose.value.replyTo) return "Répondre au message";
            if (compose.value.transferOf) return "Transférer le message";
            if (compose.value.msgType === MESSAGE_TYPE.MEMO) return "Nouveau mémo";
            if (compose.value.msgType === MESSAGE_TYPE.SMS) return "Nouveau SMS";
            return "Nouveau message";
        });
        const composeIconEmoji = computed(() => {
            if (compose.value.important) return "🚩";
            if (compose.value.msgType === MESSAGE_TYPE.MEMO) return "📝";
            if (compose.value.msgType === MESSAGE_TYPE.SMS) return "💬";
            return "📨";
        });
        const composeIconStyle = computed(() => {
            if (compose.value.important) return "background:var(--eflo-red-dim)";
            if (compose.value.msgType === MESSAGE_TYPE.MEMO) return "background:var(--eflo-teal-dim)";
            if (compose.value.msgType === MESSAGE_TYPE.SMS) return "background:var(--eflo-purple-dim)";
            return "background:var(--eflo-blue-dim)";
        });
        const sendBtnLabel = computed(() => {
            if (sendingMessage.value) return "⏳ Envoi...";
            if (compose.value.msgType === MESSAGE_TYPE.ALERT) return "🚨 Envoyer avec alerte";
            if (compose.value.important) return "🚩 Envoyer (important)";
            if (compose.value.msgType === MESSAGE_TYPE.MEMO) return "💾 Enregistrer le mémo";
            if (compose.value.msgType === MESSAGE_TYPE.SMS) return "📱 Envoyer le SMS";
            return "📤 Envoyer le message";
        });
        const isSmsMode = computed(() => compose.value.msgType === MESSAGE_TYPE.SMS);
        const smsRecipientPool = computed<any[]>(() => {
            const merged = [...smsPartyRecipients.value, ...storeRecipients.value];
            const byKey: Record<string, any> = {};
            merged.forEach((r: any) => {
                const key = `${r.objectType || "unknown"}|${r.resourceUid || r.name || getRecipientDisplayName(r)}`;
                if (!byKey[key]) byKey[key] = r;
            });
            return Object.values(byKey);
        });
        const intervenantRecipientBundles = computed<any[]>(() => {
            const fromStore = storeRecipients.value || [];
            const parties = emailPartyRecipients.value || [];
            const seenEmail = new Set<string>();
            const dedupedPartyEmails = parties.filter((r: any) => {
                const em = normalizeRecipientEmail(r.email);
                if (!em || seenEmail.has(em)) return false;
                seenEmail.add(em);
                return true;
            });
            const bundles: any[] = [];
            for (const pe of dedupedPartyEmails) {
                const u = findUserMatchingPartyEntry(fromStore, pe);
                bundles.push({
                    isIntervenantChoiceRow: true,
                    orderPartyRole: pe.orderPartyRole,
                    name: pe.name,
                    thirdResourceUid: pe.thirdResourceUid,
                    emailOption: {
                        isEmail: true,
                        email: pe.email,
                        objectType: "email",
                        resourceUid: pe.email,
                        name: pe.name,
                        orderPartyRole: pe.orderPartyRole,
                        isFromOrderParty: true,
                        thirdResourceUid: pe.thirdResourceUid,
                    },
                    userOption: u
                        ? {
                            ...u,
                            orderPartyRole: pe.orderPartyRole,
                            isOrderPartyLinkedUser: true,
                            thirdResourceUid: pe.thirdResourceUid || u.thirdResourceUid,
                        }
                        : null,
                });
            }
            return bundles;
        });
        const messageRecipientPool = computed<any[]>(() => {
            const fromStore = storeRecipients.value || [];
            const bundles = intervenantRecipientBundles.value;
            const userOt = "odm.user.leaseforgeuser";
            const bundleUserIds = new Set<string>();
            const bundleEmails = new Set<string>();
            const bundleThirdKeys = new Set<string>();
            for (const b of bundles) {
                if (b.userOption) bundleUserIds.add(String(b.userOption.resourceUid ?? ""));
                bundleEmails.add(normalizeRecipientEmail(b.emailOption?.email));
                const tk = normalizeResourceKey(b.thirdResourceUid);
                if (tk) bundleThirdKeys.add(tk);
            }
            return fromStore.filter((x: any) => {
                if (x.objectType !== userOt) return true;
                const id = String(x.resourceUid ?? "");
                const em = normalizeRecipientEmail(x.email);
                const tKey = normalizeResourceKey(x.thirdResourceUid);
                if (bundleUserIds.has(id)) return false;
                if (bundleEmails.has(em)) return false;
                if (tKey && bundleThirdKeys.has(tKey)) return false;
                return true;
            });
        });
        const filteredIntervenantBundles = computed(() => {
            if (isSmsMode.value) return [];
            let list = [...intervenantRecipientBundles.value];
            if (recipientFilter.value === "User") list = list.filter((b: any) => b.userOption);
            else if (recipientFilter.value === "Group") list = [];
            const needle = recipientSearch.value.trim().toLowerCase();
            if (needle) {
                list = list.filter((b: any) => {
                    const disp = (b.name || "").toString().toLowerCase();
                    const em = (b.emailOption?.email || "").toString().toLowerCase();
                    const uo = b.userOption;
                    const un = uo
                        ? `${uo.firstName || ""} ${uo.lastName || ""} ${uo.username || ""} ${uo.email || ""}`.toLowerCase()
                        : "";
                    const roleLabel = b.orderPartyRole
                        ? String(orderPartyRoleLabel(b.orderPartyRole)).toLowerCase()
                        : "";
                    return (
                        disp.includes(needle) ||
                        em.includes(needle) ||
                        un.includes(needle) ||
                        roleLabel.includes(needle)
                    );
                });
            }
            const userOt = "odm.user.leaseforgeuser";
            return list.filter((b: any) => {
                const em = b.emailOption?.email;
                const uid = b.userOption?.resourceUid;
                const sel = compose.value.selectedRecipients;
                const emailTaken = em && sel.some((s: any) => s.isEmail && s.email === em);
                const userTaken =
                    uid && sel.some((s: any) => String(s.resourceUid) === String(uid) && s.objectType === userOt);
                return !emailTaken && !userTaken;
            });
        });
        const hasRecipientDirectory = computed(() => {
            if (isSmsMode.value) return (smsRecipientPool.value || []).length > 0;
            return (storeRecipients.value || []).length > 0 || (emailPartyRecipients.value || []).length > 0;
        });
        const canSend = computed(() => {
            if (!isSmsMode.value && (!compose.value.subject || compose.value.subject.trim() === "")) return false;
            if (!compose.value.selectedRecipients || compose.value.selectedRecipients.length === 0) return false;
            if (compose.value.msgType === MESSAGE_TYPE.ALERT && compose.value.alertType.length === 0) return false;
            if (!compose.value.body || compose.value.body.trim() === "") return false;
            return true;
        });

        // ── Compose field actions ──
        const selectedAlertReason = computed<string>({
            get: () => compose.value.alertType[0] || "",
            set: (val: string) => {
                compose.value.alertType = val ? [val] : [];
            },
        });

        function selectMessageType(msgType: MESSAGE_TYPE) {
            compose.value.msgType = msgType;
            if (msgType !== MESSAGE_TYPE.ALERT) {
                compose.value.alertType = [];
            }
            if (msgType === MESSAGE_TYPE.SMS) {
                compose.value.subject = "";
                compose.value.selectedRefs = [];
                compose.value.selectedDocs = [];
                compose.value.attachedFile = null;
            }
        }

        function toggleRecipient(r: any) {
            const uid = r.value || r.resourceUid;
            const idx = compose.value.selectedRecipients.findIndex((x: any) => (x.value || x.resourceUid) === uid);
            if (idx >= 0) compose.value.selectedRecipients.splice(idx, 1);
            else compose.value.selectedRecipients.push(r);
        }

        // ── Recipient helpers ──
        const filteredRecipientOptions = computed(() => {
            let list: any[] = isSmsMode.value ? (smsRecipientPool.value || []) : (messageRecipientPool.value || []);
            if (isSmsMode.value) {
                list = list.filter((r: any) =>
                    r.objectType === "odm.user.leaseforgeuser" || r.isParty
                );
            } else if (recipientFilter.value === "User") {
                list = list.filter((r: any) => r.objectType === "odm.user.leaseforgeuser");
            } else if (recipientFilter.value === "Group") list = list.filter((r: any) => r.objectType === "odm.user.securitygroup");
            const needle = recipientSearch.value.trim().toLowerCase();
            if (needle) {
                list = list.filter((r: any) => {
                    const name = r.label || r.name || `${r.firstName || ''} ${r.lastName || ''}`;
                    const phones = getRecipientPhones(r).join(" ");
                    const emailStr = (r.email || "").toString().toLowerCase();
                    const uname = (r.username || "").toString().toLowerCase();
                    const roleLabel = r.orderPartyRole ? String(orderPartyRoleLabel(r.orderPartyRole)).toLowerCase() : "";
                    return name.toLowerCase().includes(needle)
                        || (r.resourceUid || "").toLowerCase().includes(needle)
                        || phones.includes(needle)
                        || emailStr.includes(needle)
                        || uname.includes(needle)
                        || roleLabel.includes(needle);
                });
            }
            list = list.filter((r: any) => {
                const uid = r.value || r.resourceUid;
                return !compose.value.selectedRecipients.some((sel: any) => (sel.value || sel.resourceUid) === uid);
            });
            return list.slice(0, 20);
        });

        function loadSmsPartyRecipients() {
            try {
                const order = store.state.orderModule?.order;
                if (!order || !Array.isArray(order.orderParties) || !orderMatchesPanelContext(order)) {
                    smsPartyRecipients.value = [];
                    return;
                }
                const parties = getAssociatedPartyInfo(order, "order") || [];
                smsPartyRecipients.value = parties.map((p: any, idx: number) => {
                    const phones = (p.contacts || [])
                        .filter((c: any) => c.label === "phonecontact")
                        .map((c: any) => normalizePhone(c.value))
                        .filter((ph: string) => isValidPhone(ph));
                    return {
                        isParty: true,
                        objectType: "odm.party.party",
                        resourceUid: `party-${idx}-${p.associated || ""}-${p.name || ""}`,
                        role: p.associated,
                        name: p.name,
                        phones: [...new Set(phones)],
                    };
                });
            } catch (e) {
                console.error("loadSmsPartyRecipients", e);
                smsPartyRecipients.value = [];
            }
        }

        function orderMatchesPanelContext(order: any): boolean {
            if (props.orderId && order.resourceUid) {
                return String(props.orderId) === String(order.resourceUid);
            }
            if (props.orderReference) {
                const oRef = String(order.reference ?? "").trim();
                const pRef = String(props.orderReference).trim();
                return oRef === "" || pRef === "" || oRef === pRef;
            }
            return true;
        }

        function loadEmailPartyRecipients() {
            try {
                if (props.orderReference || props.orderId) {
                    const order = store.state.orderModule?.order;
                    if (order && Array.isArray(order.orderParties) && orderMatchesPanelContext(order)) {
                        const parties = getAssociatedPartyInfo(order, "order") || [];
                        const out: any[] = [];
                        for (const p of parties) {
                            const emails = (p.contacts || []).filter(
                                (c: any) =>
                                    c.label === "emailcontact" &&
                                    c.value &&
                                    isValidEmail(String(c.value).trim())
                            );
                            for (const c of emails) {
                                const email = String(c.value).trim();
                                out.push({
                                    isEmail: true,
                                    email,
                                    objectType: "email",
                                    resourceUid: email,
                                    name: p.name,
                                    orderPartyRole: p.associated,
                                    isFromOrderParty: true,
                                    thirdResourceUid: p.thirdResourceUid || "",
                                });
                            }
                        }
                        emailPartyRecipients.value = out;
                        return;
                    }
                }
                emailPartyRecipients.value = [];
            } catch (e) {
                console.error("loadEmailPartyRecipients", e);
                emailPartyRecipients.value = [];
            }
        }

        function selectRecipient(r: any) { compose.value.selectedRecipients.push(r); recipientSearch.value = ""; recipDropOpen.value = false; }
        function selectIntervenantChannel(bundle: any, channel: "user" | "email") {
            if (channel === "user" && bundle.userOption) {
                compose.value.selectedRecipients.push({ ...bundle.userOption });
            } else if (channel === "email" && bundle.emailOption) {
                compose.value.selectedRecipients.push({ ...bundle.emailOption });
            }
            recipientSearch.value = "";
            recipDropOpen.value = false;
        }
        function removeRecipient(idx: number) { compose.value.selectedRecipients.splice(idx, 1); }

        function addEmailRecipient() {
            const email = recipientSearch.value.trim();
            if (!email || !isValidEmail(email)) return;
            if (compose.value.selectedRecipients.some((r: any) => r.isEmail && r.email === email)) { recipientSearch.value = ""; return; }
            compose.value.selectedRecipients.push({ isEmail: true, email, objectType: "email", resourceUid: email });
            recipientSearch.value = ""; recipDropOpen.value = false;
        }
        function addPhoneRecipient() {
            const phone = normalizePhone(recipientSearch.value.trim());
            if (!phone || !isValidPhone(phone)) return;
            if (compose.value.selectedRecipients.some((r: any) => r.isPhone && r.phone === phone)) { recipientSearch.value = ""; return; }
            compose.value.selectedRecipients.push({ isPhone: true, phone, objectType: "phone", resourceUid: phone });
            recipientSearch.value = ""; recipDropOpen.value = false;
        }
        function addRecipientFromSearch() {
            if (isSmsMode.value) addPhoneRecipient();
            else addEmailRecipient();
        }

        function onRecipDocClick(e: MouseEvent) { if (!(e.target as HTMLElement).closest(".recip-search-wrap")) { recipDropOpen.value = false; } }

        function toggleDoc(uid: string) { const idx = compose.value.selectedDocs.indexOf(uid); if (idx >= 0) compose.value.selectedDocs.splice(idx, 1); else compose.value.selectedDocs.push(uid); }
        function onFileChange(e: Event) { compose.value.attachedFile = (e.target as HTMLInputElement).files?.[0] ?? null; }
        function onDocumentsSelected(newVal: string[]) { compose.value.selectedDocs = newVal; }
        function removeDocument(resourceUid: string) { const idx = compose.value.selectedDocs.indexOf(resourceUid); if (idx >= 0) { compose.value.selectedDocs.splice(idx, 1); } }

        // ── Panel control ──
        function close() { isOpen.value = false; emit("update:modelValue", false); closeDetail(); }

        async function openDetail(msg: any) {
            if (msg._isAlert) return;
            if (msg._isEmail) { openEmail(msg); return; }
            selectedMessage.value = msg;
            await loadMessageThread(msg);
            // EFLO-915 : marquer lus tous les messages non lus de la conversation, pas seulement la racine,
            // sinon les réponses restent comptées dans "Non lus" indéfiniment.
            const toMark = unreadMessagesOf(msg);
            if (!isRead(msg) && !toMark.includes(msg)) toMark.push(msg);
            for (const m of toMark) await markAsRead(m);
        }
        function closeDetail() { selectedMessage.value = null; }

        function openCompose(defaults?: Partial<ReturnType<typeof makeCompose>>) {
            compose.value = Object.assign(makeCompose(), defaults || {});
            composeError.value = "";
            refSearch.value = "";
            refDropOpen.value = false;
            refFilteredOptions.value = props.referenceOptions.slice(0, 20);
            showCompose.value = true;
            prefillDefaultOrderRef();
            loadSmsPartyRecipients();
            loadEmailPartyRecipients();
        }

        function closeCompose() {
            showCompose.value = false;
            setTimeout(() => {
                const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
                if (fileInput) fileInput.value = "";
                compose.value = makeCompose();
                composeError.value = "";
            }, 100);
        }

        function getReplyRecipient(msg: any, currentUserId: string | undefined): any {
            const msgSenderUid = msg.senderUid?.resourceUid || msg.senderUid || msg.senderuid?.resourceUid || msg.senderuid || "";
            if (msgSenderUid && msgSenderUid !== currentUserId) {
                return {
                    resourceUid: msgSenderUid,
                    objectType: msg.senderUid?.objectType || "odm.user.leaseforgeuser",
                    systemUid: msg.senderUid?.systemUid || "odm-user",
                    firstName: msg.sender?.split(" ")[0] || "",
                    lastName: msg.sender?.split(" ").slice(1).join(" ") || "",
                };
            }

            const recipients = Array.isArray(msg.recipients) ? msg.recipients : [];
            const found = recipients.find((r: any) => (r.recipient?.resourceUid || r.resourceUid) !== currentUserId) || recipients[0];
            const other = found?.recipient || found;
            if (!other?.resourceUid) return null;
            const directoryMatch = storeRecipients.value.find((r: any) => r.resourceUid === other.resourceUid);
            return {
                resourceUid: other.resourceUid,
                objectType: other.objectType || "odm.user.leaseforgeuser",
                systemUid: other.systemUid || "odm-user",
                firstName: directoryMatch?.firstName || "",
                lastName: directoryMatch?.lastName || "",
            };
        }

        function startReply(msg: any) {
            store.dispatch("notificationModule/setMsgUID", msg.resourceUid);
            const subject = msg.subject?.resourceUid || msg.subject || "";
            const currentUserId = store.state.authModule?.user?.userId;
            const replyRecipients: any[] = [];
            const other = getReplyRecipient(msg, currentUserId);
            if (other) replyRecipients.push(other);
            openCompose({ msgType: MESSAGE_TYPE.MESSAGE, replyTo: msg, subject, selectedRecipients: replyRecipients });
        }

        function startTransfer(msg: any) {
            store.dispatch("notificationModule/setMsgUID", msg.resourceUid);
            openCompose({ msgType: MESSAGE_TYPE.MESSAGE, transferOf: msg, subject: msg.subject?.resourceUid || msg.subject || "", body: `\n\n-------- Message transféré --------\nDe : ${msg.sender}\n\n${msg.body || msg.content || ""}` });
        }

        // ── Send logic ──
        function resolveOrderContextRow(refObj: any): any {
            let row: any = refObj ? { ...refObj } : {};
            if (refObj?.referenceorder && props.referenceOptions?.length) {
                const found = findOrderRefInOptions(
                    props.referenceOptions,
                    refObj.referenceorder,
                    refObj.externalreference || refObj.externalReference || "",
                    refObj.resourceUid || props.orderId,
                );
                if (found) row = { ...found, ...row };
            }
            const storeRow = mapOrderModuleStateToContextRow();
            if (storeRow) {
                const refKey = normRefKey(row.referenceorder || props.orderReference);
                const storeRefKey = normRefKey(String(pickOrderRowField(storeRow, "referenceorder", "referenceOrder") ?? ""));
                if (!refKey || refKey === storeRefKey) row = { ...storeRow, ...row };
            }
            return row;
        }

        function buildParameters(refObj: any) {
            const alertType = compose.value.alertType.length > 0 ? compose.value.alertType.join(",") : null;
            if (refObj?.referenceorder) {
                const entityId = store.getters["authModule/getSelectedUserNetwork"];
                const orderRow = resolveOrderContextRow(refObj);
                return {
                    Context: "ORDER",
                    Entity_Reference: refObj.referenceorder,
                    Entity_ExternalReference: refObj.externalreference || "",
                    Entity_ResourceUid: resolveOrderResourceUid(refObj, props.orderId, props.orderReference),
                    Entity_Daaq: refObj._daaq,
                    Entity_Status: refObj.status_code,
                    Entity_Id: entityId?.value,
                    Alert_Type: alertType,
                    ...buildOrderContextParameters(orderRow),
                };
            }
            if (refObj?.id && !refObj?.reference) {
                return { Context: "PARTY", Entity_Reference: refObj.reference || refObj.id, Entity_ExternalReference: refObj.externalreference || "", Entity_ResourceUid: refObj.resourceUid || "", Entity_Daaq: refObj._daaq, Entity_Status: refObj.statuscode || refObj.current_status_code || "", Alert_Type: alertType };
            }
            if (refObj?.reference) {
                return { Context: "OFFER", Entity_Reference: refObj.reference, Entity_ExternalReference: refObj.externalreference || "", Entity_ResourceUid: refObj.resourceUid || "", Entity_Daaq: refObj._daaq, Entity_Status: refObj.statuscode || refObj.current_status_code || "", Alert_Type: alertType };
            }
            return { Context: null, Entity_Reference: null, Entity_ExternalReference: null, Entity_ResourceUid: null, Entity_Daaq: null, Entity_Status: null };
        }

        function buildMultiOrderParameters(refs: any[]) {
            const entityId = store.getters["authModule/getSelectedUserNetwork"];
            const rows = refs.map((r) => resolveOrderContextRow(r));
            return {
                Context: "ORDER",
                Entity_Reference: refs.map((r) => r.referenceorder).join(","),
                Entity_ExternalReference: refs.map((r) => r.externalreference || "").join(","),
                Entity_ResourceUid: refs.map((r) => resolveOrderResourceUid(r, props.orderId, props.orderReference)).join(","),
                Entity_Daaq: "/",
                Entity_Status: "ORDER",
                Entity_Id: entityId?.value,
                Alert_Type: compose.value.alertType.length > 0 ? compose.value.alertType.join(",") : null,
                ...joinOrderContextParameters(rows),
            };
        }

        /** Libellés des destinataires choisis (utilisateur, groupe ou email), pour l'affichage « À : ». */
        function selectedRecipientLabels(): string {
            return (compose.value.selectedRecipients || [])
                .map((r: any) => getRecipientDisplayName(r))
                .filter((n: string) => n)
                .join(", ");
        }

        function buildPayload(parameters: any) {
            const _recipients: recipient[] = convertToUON(compose.value.selectedRecipients.filter((r: any) => r.objectType !== "email"));
            _recipients.forEach((el: any) => { delete el.recipient?.read_daaq; delete el.recipient?.email; });
            return { objectType: objectType_odmTransferReplyMsg, systemUid, daaq: `${store.state.authModule?.daaq}${store.state.authModule?.daaqResourceUid}/${store.state.authModule?.daaqLabel}`, sender: `${store.state.authModule?.user?.firstName} ${store.state.authModule?.user?.lastName}`, senderUid: { ...senderUid, resourceUid: store.state.authModule?.user?.userId }, priority: compose.value.important, subject: { ...subjectConst, resourceUid: compose.value.subject }, type: typeMsg, recipients: _recipients, body: compose.value.body, relatedObjects: null, clientApplication: clientAppMsg, messageTemplate, parameters: { ...parameters, Recipients: selectedRecipientLabels() } };
        }

        function validateCompose(): string {
            if (!isSmsMode.value && !compose.value.subject) return "Veuillez sélectionner un sujet.";
            if (compose.value.msgType !== MESSAGE_TYPE.MEMO && compose.value.selectedRecipients.length === 0) return "Veuillez sélectionner au moins un destinataire.";
            if (compose.value.msgType === MESSAGE_TYPE.ALERT && compose.value.alertType.length === 0) return "Veuillez sélectionner un motif d'alerte.";
            if (!compose.value.body.trim()) return "Le message ne peut pas être vide.";
            if (!store.state.authModule?.user?.userId) return "Utilisateur non authentifié.";
            return "";
        }

        function buildEmailSendTemplateParameters() {
            const sender = `${store.state.authModule?.user?.firstName} ${store.state.authModule?.user?.lastName}`;
            const body = compose.value.body;
            const subject = compose.value.subject || "";
            const refs = compose.value.selectedRefs;
            let entityParams: Record<string, any> = {
                Context: null,
                Entity_Reference: null,
                Entity_ExternalReference: null,
                Entity_ResourceUid: null,
                Entity_Daaq: null,
                Entity_Status: null,
            };
            if (compose.value.replyTo) {
                entityParams = {
                    Context: messageFieldValue(compose.value.replyTo, "Context"),
                    Entity_Reference: messageFieldValue(compose.value.replyTo, "Entity_Reference"),
                    Entity_ExternalReference: messageFieldValue(compose.value.replyTo, "Entity_ExternalReference"),
                    Entity_ResourceUid: messageFieldValue(compose.value.replyTo, "Entity_ResourceUid"),
                    Entity_Status: messageFieldValue(compose.value.replyTo, "Entity_Status"),
                    Entity_Daaq: messageFieldValue(compose.value.replyTo, "Entity_Daaq"),
                    ...extractOrderContextFromMessage(compose.value.replyTo),
                };
            } else if (compose.value.transferOf) {
                if (refs.length > 0) {
                    entityParams = buildParameters(refs[0]);
                } else {
                    entityParams = {
                        Context: messageFieldValue(compose.value.transferOf, "Context"),
                        Entity_Reference: messageFieldValue(compose.value.transferOf, "Entity_Reference"),
                        Entity_ExternalReference: messageFieldValue(compose.value.transferOf, "Entity_ExternalReference"),
                        Entity_ResourceUid: messageFieldValue(compose.value.transferOf, "Entity_ResourceUid"),
                        Entity_Status: messageFieldValue(compose.value.transferOf, "Entity_Status"),
                        Entity_Daaq: messageFieldValue(compose.value.transferOf, "Entity_Daaq"),
                        ...extractOrderContextFromMessage(compose.value.transferOf),
                    };
                }
            } else if (refs.length > 1 && refs.every((r: any) => r.referenceorder !== undefined && r.referenceorder !== null)) {
                entityParams = buildMultiOrderParameters(refs);
            } else if (refs.length > 0) {
                entityParams = buildParameters(refs[0]);
            } else if (props.orderReference) {
                entityParams = buildParameters({
                    referenceorder: props.orderReference,
                    externalreference: getOrderExternalRef(),
                    resourceUid: props.orderId,
                    _daaq: store.state.orderModule?.order?._daaq,
                    status_code: store.state.orderModule?.order?.status?.resourceUid,
                });
            }
            return { body, sender, subject, ...entityParams };
        }

        async function triggerEmailSend() {
            const externalEmails: string[] = compose.value.selectedRecipients.filter((r: any) => r.isEmail && r.email).map((r: any) => r.email as string);
            const userEmails: string[] = compose.value.selectedRecipients.filter((r: any) => r.objectType === "odm.user.leaseforgeuser" && r.email).map((r: any) => r.email as string);
            const hasAttachments = compose.value.attachedFile || compose.value.selectedDocs.length > 0;
            const emailRecipients: string[] = hasAttachments ? [...userEmails, ...externalEmails] : externalEmails;
            if (!emailRecipients.length) return;
            const fileReferences: string[] = compose.value.selectedDocs;
            const attachedFile = compose.value.attachedFile ? await fileToEmailAttachment(compose.value.attachedFile) : null;
            const templateParameters = buildEmailSendTemplateParameters();
            await sendMailWithDocument(emailRecipients, attachedFile, fileReferences, templateParameters);
        }

        function mapInternalRecipients() {
            return compose.value.selectedRecipients
                .filter((r: any) => r.objectType !== "email")
                .map((r: any) => ({
                    recipient: {
                        objectType: r.objectType || "odm.user.leaseforgeuser",
                        systemUid: r.systemUid || "odm-user",
                        resourceUid: r.resourceUid,
                    },
                }));
        }

        function getSubjectObject(originalSubject: any) {
            return typeof originalSubject === "object" && originalSubject?.objectType
                ? originalSubject
                : { objectType: "odm.contactmechanism.messagesubject", systemUid: "odm-contactmechanism", resourceUid: compose.value.subject };
        }

        async function sendReplyMessage() {
            const _recipients = mapInternalRecipients();
            const root = msgRootForAction(compose.value.replyTo);
            const replyParameters = {
                Context: messageFieldValue(compose.value.replyTo, "Context"),
                Entity_Reference: messageFieldValue(compose.value.replyTo, "Entity_Reference"),
                Entity_ExternalReference: messageFieldValue(compose.value.replyTo, "Entity_ExternalReference"),
                Entity_ResourceUid: messageFieldValue(compose.value.replyTo, "Entity_ResourceUid"),
                Entity_Daaq: messageFieldValue(compose.value.replyTo, "Entity_Daaq"),
                Entity_Status: messageFieldValue(compose.value.replyTo, "Entity_Status"),
                ...extractOrderContextFromMessage(compose.value.replyTo),
                MSG_ROOT: root,
            };
            const replyPayload = {
                resourceUid: compose.value.replyTo.resourceUid,
                messageTemplate: { objectType: "odm.contactmechanism.messagetemplate", systemUid: "odm-contactmechanism", resourceUid: "TEMPLATE_MSG" },
                sender: `${store.state.authModule?.user?.firstName} ${store.state.authModule?.user?.lastName}`,
                senderUid: { objectType: "odm.user.leaseforgeuser", systemUid: "odm-user", resourceUid: store.state.authModule?.user?.userId },
                priority: compose.value.important,
                subject: getSubjectObject(compose.value.replyTo?.subject),
                type: { objectType: "odm.contactmechanism.messagetype", systemUid: "odm-contactmechanism", resourceUid: "MESSAGE" },
                body: compose.value.body,
                recipients: _recipients,
                parameters: replyParameters,
                relatedObjects: null,
                clientApplication: { objectType: "odm.contactmechanism.contactmechanismclient", systemUid: "odm-contactmechanism", resourceUid: "68815" },
                objectType: objectType_odmTransferReplyMsg,
                systemUid: "odm-contactmechanism",
                daaq: `${store.state.authModule?.daaq}${store.state.authModule?.daaqResourceUid}/${store.state.authModule?.daaqLabel}`,
            };
            await store.dispatch("notificationModule/messageReply", replyPayload);
        }

        async function sendTransferMessage(refs: any[]) {
            const _recipients = mapInternalRecipients();
            const root = msgRootForAction(compose.value.transferOf);
            const sourceTransferParams = {
                Context: messageFieldValue(compose.value.transferOf, "Context"),
                Entity_Reference: messageFieldValue(compose.value.transferOf, "Entity_Reference"),
                Entity_ExternalReference: messageFieldValue(compose.value.transferOf, "Entity_ExternalReference"),
                Entity_ResourceUid: messageFieldValue(compose.value.transferOf, "Entity_ResourceUid"),
                Entity_Daaq: messageFieldValue(compose.value.transferOf, "Entity_Daaq"),
                Entity_Status: messageFieldValue(compose.value.transferOf, "Entity_Status"),
                ...extractOrderContextFromMessage(compose.value.transferOf),
            };
            const transferPayload = {
                objectType: objectType_odmTransferReplyMsg,
                systemUid: "odm-contactmechanism",
                daaq: `${store.state.authModule?.daaq}${store.state.authModule?.daaqResourceUid}/${store.state.authModule?.daaqLabel}`,
                resourceUid: compose.value.transferOf.resourceUid,
                messageTemplate: { objectType: "odm.contactmechanism.messagetemplate", systemUid: "odm-contactmechanism", resourceUid: "TEMPLATE_MSG" },
                sender: `${store.state.authModule?.user?.firstName} ${store.state.authModule?.user?.lastName}`,
                senderUid: { objectType: "odm.user.leaseforgeuser", systemUid: "odm-user", resourceUid: store.state.authModule?.user?.userId },
                priority: compose.value.important,
                subject: getSubjectObject(compose.value.transferOf?.subject),
                type: { objectType: "odm.contactmechanism.messagetype", systemUid: "odm-contactmechanism", resourceUid: "MESSAGE" },
                body: compose.value.body,
                recipients: _recipients,
                parameters: withMsgRoot(refs.length > 0 ? buildParameters(refs[0]) : sourceTransferParams, root),
                relatedObjects: null,
                clientApplication: { objectType: "odm.contactmechanism.contactmechanismclient", systemUid: "odm-contactmechanism", resourceUid: "68815" },
            };
            await store.dispatch("notificationModule/transferMessage", transferPayload);
        }

        async function sendStandardMessage(refs: any[], alerts: any[]) {
            if (!refs.length) {
                await store.dispatch("messagingModule/messageSend", buildPayload(withMsgRoot({ Context: null, Entity_Reference: null, Entity_ExternalReference: null, Entity_ResourceUid: null, Entity_Daaq: null, Entity_Status: null }, null)));
            } else if (refs.length > 1 && refs.every((r) => r.referenceorder !== undefined && r.referenceorder !== null)) {
                await store.dispatch("messagingModule/messageSend", buildPayload(withMsgRoot(buildMultiOrderParameters(refs), null)));
            } else {
                await Promise.all(refs.map((ref) => store.dispatch("messagingModule/messageSend", buildPayload(withMsgRoot(buildParameters(ref), null)))));
            }
            if (alerts.length > 0) {
                const orderRefs = refs.filter((r) => r.referenceorder);
                if (orderRefs.length > 0) {
                    await Promise.all(orderRefs.map((ref) => store.dispatch("orderModule/createOrderAlert", {
                        orderId: resolveOrderResourceUid(ref, props.orderId, props.orderReference) || ref.orderId || ref.orderid || ref.resourceUid,
                        reasons: alerts,
                        comment: compose.value.body,
                        recipients: selectedRecipientLabels(),
                    })));
                }
            }
        }

        async function sendSmsMessage() {
            const recipientsWithPhones = compose.value.selectedRecipients
                .map((r: any) => ({ phones: r.isPhone ? [normalizePhone(r.phone)] : getRecipientPhones(r) }))
                .filter((x: any) => x.phones.length > 0);
            if (!recipientsWithPhones.length) throw new Error("Aucun numéro de téléphone valide n'a été trouvé.");

            await Promise.all(
                recipientsWithPhones.flatMap((entry: any) =>
                    entry.phones.map((phone: string) =>
                        sendSms({
                            objectType: "odm.contactmechanism.communicationrequest.smsmessage",
                            smsTemplate: {
                                "objectType": "SmsTemplate",
                                "systemUid": "systemUid",
                                "resourceUid": "SMS_NOTIFICATION"
                            },
                            clientApplication: {
                                "objectType": "odm.contactmechanism.contactmechanismclient",
                                "systemUid": "odm-contactmechanism",
                                "resourceUid": "SMS_NOTIFICATION"
                            },
                            parameters: {
                                recipient: phone,
                                sender: `${store.state.authModule?.user?.firstName} ${store.state.authModule?.user?.lastName}`,
                                sms_body: compose.value.body,
                                order_reference: props.orderReference || "",
                            },
                        })
                    )
                )
            );
        }

        async function sendMessage() {
            composeError.value = validateCompose();
            if (composeError.value || sendingMessage.value) return;
            sendingMessage.value = true;
            try {
                const refs = compose.value.selectedRefs;
                const alerts = compose.value.alertType;
                if (compose.value.replyTo) {
                    await sendReplyMessage();
                } else if (compose.value.transferOf) {
                    await sendTransferMessage(refs);
                } else if (isSmsMode.value) {
                    await sendSmsMessage();
                } else {
                    await sendStandardMessage(refs, alerts);
                }
                if (!isSmsMode.value) await triggerEmailSend();
                closeCompose();
                store.dispatch("notificationModule/fetchData", null);
                if (props.orderReference) { store.dispatch("messagingModule/historicMessages", { type: "MESSAGE", entityId: props.orderReference }); }
                emit("message-sent");
            } catch (e) { console.error("sendMessage", e); composeError.value = "Une erreur est survenue lors de l'envoi."; }
            finally { sendingMessage.value = false; }
        }

        // ── Mark actions ──
        async function markAsRead(msg: any) {
            if (markingRead.value) return;
            markingRead.value = true;
            try {
                const notifications = allNotifications.value;
                const index = notifications.findIndex((n: any) => n.resourceUid === msg.resourceUid);
                if (index >= 0) { await store.dispatch("notificationModule/updateStateOfMessage", { index, notification: msg, ActionType: "ChangeStatus" }); }
                if (msg.status) msg.status.resourceUid = "READ"; else msg.status = { resourceUid: "READ" };
            } catch (e) { console.error("markAsRead", e); } finally { markingRead.value = false; }
        }

        async function markAsTreated(msg: any) {
            if (markingTreated.value) return;
            markingTreated.value = true;
            try {
                const notifications = allNotifications.value;
                const index = notifications.findIndex((n: any) => n.resourceUid === msg.resourceUid);
                if (index >= 0) { await store.dispatch("notificationModule/updateStateOfMessage", { index, notification: msg, ActionType: "CloseStatus" }); }
                if (msg.status) msg.status.resourceUid = "CLOSED"; else msg.status = { resourceUid: "CLOSED" };
            } catch (e) { console.error("markAsTreated", e); } finally { markingTreated.value = false; }
        }

        function deleteMessage(msg: any, event?: Event) {
            if (event) event.stopPropagation();
            if (msg._isAlert) {
                store.dispatch("showConfirmationDeleteDialog", { message: "Are you sure you want to delete this alert", confirmation: true, payload: { alert: { alertValidity: msg._alertValidity, comment: msg.body || msg.content || "", reasons: (msg._alertReasons || []).join(",") }, orderId: msg._orderId || props.orderReference, id: msg._alertId }, deleteAction: "RemoveAlert" });
                return;
            }
            const notifications = allNotifications.value;
            const index = notifications.findIndex((n: any) => n.resourceUid === msg.resourceUid);
            if (index >= 0) { store.dispatch("notificationModule/updateState", { index, notification: msg, ActionType: "Delete" }); }
            else { console.error("deleteMessage: Could not find notification for", msg.resourceUid); }
        }

        async function deleteAllFilteredMessages() {
            if (deletingAll.value || filteredMessages.value.length === 0) return;
            const accepted = globalThis.confirm(`Supprimer tous les éléments affichés (${filteredMessages.value.length}) ?`);
            if (!accepted) return;

            deletingAll.value = true;
            try {
                const currentList = [...filteredMessages.value];
                const notifications = allNotifications.value;

                for (const msg of currentList) {
                    if (msg._isAlert) {
                        await store.dispatch("orderModule/deleteOrderAlert", {
                            alert: {
                                alertValidity: msg._alertValidity,
                                comment: msg.body || msg.content || "",
                                reasons: (msg._alertReasons || []).join(","),
                            },
                            alertId: msg._alertId,
                            orderId: msg._orderId || props.orderReference,
                        });
                        continue;
                    }

                    const index = notifications.findIndex((n: any) => n.resourceUid === msg.resourceUid);
                    if (index >= 0) {
                        await store.dispatch("notificationModule/updateStateOfMessage", {
                            index,
                            notification: msg,
                            ActionType: "Delete",
                        });
                    }
                }

                if (selectedMessage.value && currentList.some((m: any) => m.resourceUid === selectedMessage.value?.resourceUid)) {
                    closeDetail();
                }
                store.dispatch("notificationModule/fetchData", null);
            } catch (e) {
                console.error("deleteAllFilteredMessages", e);
            } finally {
                deletingAll.value = false;
            }
        }

        function loadAlertReasons() {
            store.dispatch("orderModule/getAlertReasons", {
                callback: (res: any) => {
                    if (res?.items) {
                        alertTypes.value = res.items
                            .filter((item: any) => item.id !== "COMMISSION_MODIFIEE")
                            .map((item: any) => ({ key: item.id, label: item.id, cssClass: alertCssClass(item.id) }));
                    }
                },
            });
        }

        function getReplyCount(msg: any): number {
            if (msg._replyCount !== undefined) return msg._replyCount;
            const key = threadKeyForMessage(msg);
            const g = threadGroups.value[key];
            if (g && g.root && (g.root.resourceUid || g.root.resourceuid) === (msg.resourceUid || msg.resourceuid)) {
                return Math.max(0, g.messages.length - 1);
            }
            const pack = historicMessages.value.find((p: any) => p.messagePack?.some((m: any) => (m.resourceuid || m.resourceUid) === msg.resourceUid));
            if (pack && pack.messagePack) return Math.max(0, pack.messagePack.length - 1);
            return 0;
        }

        // ── Watchers ──
        watch(() => props.modelValue, (val) => {
            isOpen.value = val;
            if (val) {
                store.dispatch("notificationModule/fetchData", null);
                store.dispatch("messagingModule/fetchRecipients");
                store.dispatch("messagingModule/fetchOrderUserEmails");
                if (props.orderReference) { store.dispatch("messagingModule/historicMessages", { type: "MESSAGE", entityId: props.orderReference }); }
            }
        });
        watch(isOpen, (val) => { if (!val) emit("update:modelValue", false); });
        watch(() => props.orderReference, (newRef) => {
            activeTab.value = "all"; readFilter.value = "all"; searchInput.value = ""; selectedMessage.value = null; selectedEmail.value = null;
            if (newRef) { store.dispatch("messagingModule/historicMessages", { type: "MESSAGE", entityId: newRef }); }
            loadSmsPartyRecipients();
            loadEmailPartyRecipients();
        });
        watch(
            () => (store.state as any).orderModule?.order?.resourceUid,
            () => {
                loadSmsPartyRecipients();
                loadEmailPartyRecipients();
            }
        );
        watch(() => props.referenceOptions, (val) => { if (!refSearch.value) refFilteredOptions.value = (val || []).slice(0, 20); }, { immediate: true });

        const totalUnreadCount = computed(() => unreadCount.value + orderAlerts.value.filter((a: any) => !isDeletedItem(a)).length);
        const totalMessagesCount = computed(() => tabCount("all"));
        watch(totalUnreadCount, (val) => { emit("update:unreadCount", val); }, { immediate: true });
        watch(totalMessagesCount, (val) => { emit("update:totalMessagesCount", val); }, { immediate: true });

        // ── Lifecycle ──
        onMounted(() => {
            store.dispatch("notificationModule/fetchData", null);
            store.dispatch("messagingModule/fetchRecipients");
            store.dispatch("messagingModule/fetchOrderUserEmails");
            loadAlertReasons();
            loadSmsPartyRecipients();
            loadEmailPartyRecipients();
            if (props.orderReference) { store.dispatch("messagingModule/historicMessages", { type: "MESSAGE", entityId: props.orderReference }); }
            document.addEventListener("click", onDocClick);
            document.addEventListener("click", onRecipDocClick);
        });
        onBeforeUnmount(() => {
            document.removeEventListener("click", onDocClick);
            document.removeEventListener("click", onRecipDocClick);
            if (refSearchTimer) clearTimeout(refSearchTimer);
        });

        const showMessageOrderKpiStripForMsg = (msg: any) => showMessageOrderKpiStrip(msg, props.orderId);

        return {
            MESSAGE_TYPE: MESSAGE_TYPE_REF,
            isOpen, activeTab, readFilter, searchInput,
            sortBy, sortDirection, deletingAll,
            selectedMessage, showCompose, markingRead, markingTreated,
            sendingMessage, compose, composeError,
            refSearch, refDropOpen, refFilteredOptions, refLoading,
            recipientSearch, recipDropOpen, recipientFilter, filteredRecipientOptions, filteredIntervenantBundles,
            selectRecipient, selectIntervenantChannel, removeRecipient, getRecipientTypeLabel, getRecipientTypeClass, getRecipientDisplayName,
            orderPartyRoleLabel, recipientSuggestionKey,
            getPrimaryRecipientPhone, recipientPhoneHint,
            addEmailRecipient, addRecipientFromSearch,
            tabs, readFilters, visibleReadFilters, msgTypes, visibleMsgTypes, alertTypes,
            storeRecipients, historicMessages, parentMessages,
            filteredMessages, unreadCount, flattenedDocuments,
            composeTitle, composeIconEmoji, composeIconStyle, sendBtnLabel, canSend, isSmsMode, hasRecipientDirectory,
            tabCount,
            subjectText, bodyExcerpt, channelText, attachmentCount, formatDate,
            isDeletedItem, deletedByOf, deletedAtOf, recipientLabel,
            isRead, threadHasUnread, isTreated, canMarkStatus, canMarkRead, statusLabel, statusTagClass,
            msgIcon, msgCardBorderClass, avatarColorClass, typeLabel, typeTagClass, alertBadgeLabels,
            showMessageOrderKpiStrip: showMessageOrderKpiStripForMsg, messageOrderKpiPills,
            refContext, refLabel, refBadgeClass, onRefSearch, onRefInputFocus, selectRef, removeRef,
            onRecipInputFocus,
            selectedAlertReason, selectMessageType,
            toggleRecipient, toggleDoc, onFileChange, onDocumentsSelected, removeDocument,
            close, openDetail, closeDetail, openCompose, closeCompose,
            startReply, startTransfer, markAsRead, markAsTreated, sendMessage,
            deleteMessage, deleteAllFilteredMessages, getReplyCount,
            toggleSortDirection,
            threadRef, loadingThread, loadMessageThread,
            isCurrentUser, senderInitials, bubbleAvatarClass, bubbleClass, threadRefLabel,
            messageThread, showDateSeparator, formatDateSep, isAlert, loadAlertReasons, alertCssClass,
            orderAlerts, openOrderTab, resolveLinkToOrder,
            docSearch, filteredFlattenedDocuments, toggleDocItem,
            isThreadRoot,
            orderEmails, selectedEmail, openEmail, closeEmail, emailIsHtml,
        };
    },
});
</script>

<style scoped>
.eflo-msg-sidebar,
.eflo-detail-drawer,
.eflo-overlay,
.eflo-sidebar-overlay {
    --eflo-navy: #00073F;
    --eflo-accent: #F5A800;
    --eflo-accent-2: #D99200;
    --eflo-accent-dim: rgba(245, 168, 0, 0.10);
    --eflo-blue: #0060F0;
    --eflo-blue-dim: rgba(0, 96, 240, 0.09);
    --eflo-blue-light: #3D82F5;
    --eflo-teal: #00C49A;
    --eflo-teal-dim: rgba(0, 196, 154, 0.10);
    --eflo-red: #E8304A;
    --eflo-red-dim: rgba(232, 48, 74, 0.09);
    --eflo-orange: #F5A800;
    --eflo-orange-dim: rgba(245, 168, 0, 0.10);
    --eflo-green: #17A05C;
    --eflo-green-dim: rgba(23, 160, 92, 0.10);
    --eflo-purple: #7C3AED;
    --eflo-purple-dim: rgba(124, 58, 237, 0.09);
    --eflo-surface: #FFFFFF;
    --eflo-surface-2: #F5F7FC;
    --eflo-surface-3: #EBEEF6;
    --eflo-border: #DDE2EE;
    --eflo-border-2: #C8CFDF;
    --eflo-text: #00073F;
    --eflo-text-2: #4A5580;
    --eflo-text-3: #9BA8C4;
    --eflo-shadow-sm: 0 1px 3px rgba(0, 7, 63, 0.07);
    --eflo-shadow: 0 2px 8px rgba(0, 7, 63, 0.09);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 13px;
    color: var(--eflo-text);
    box-sizing: border-box;
}

/* ══ OVERLAY ══ */
.eflo-sidebar-overlay {
    position: fixed;
    inset: 0;
    background: rgba(24, 33, 58, 0.28);
    backdrop-filter: blur(3px);
    z-index: 3333;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s;
}

.eflo-sidebar-overlay.open {
    opacity: 1;
    pointer-events: all;
}

/* ══ SIDEBAR ══ */
.eflo-msg-sidebar {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 600px;
    background: var(--eflo-surface);
    border-left: 1px solid var(--eflo-border);
    box-shadow: -16px 0 60px rgba(24, 33, 58, 0.18);
    z-index: 3333;
    display: flex;
    flex-direction: column;
    transform: translateX(100%);
    transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
}

/* Mode inline : le panneau occupe la largeur de son onglet au lieu de glisser
   depuis la droite. Position statique, sans ombre ni translation. */
.eflo-msg-sidebar.is-inline {
    position: static;
    width: 100%;
    height: 620px;
    max-height: calc(100vh - 260px);
    border-left: none;
    box-shadow: none;
    transform: none;
    transition: none;
    z-index: auto;
}

.eflo-msg-sidebar.open {
    transform: translateX(0);
}

.ms-header {
    padding: 0 20px;
    height: 63px;
    border-bottom: 1px solid var(--eflo-border);
    display: flex;
    align-items: center;
    gap: 12px;
    background: var(--eflo-surface);
    flex-shrink: 0;
    box-shadow: var(--eflo-shadow-sm);
}

.ms-header-icon {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: var(--eflo-accent-dim);
    border: 1px solid rgba(245, 168, 0, 0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--eflo-accent-2);
    font-size: 15px;
    flex-shrink: 0;
}

.ms-title {
    font-size: 14px;
    font-weight: 700;
    line-height: 1.2;
}

.ms-subtitle {
    font-size: 13px;
    color: var(--eflo-orange);
    margin-top: 2px;
    font-family: 'JetBrains Mono', monospace;
}

.ms-close {
    width: 30px;
    height: 30px;
    border-radius: 7px;
    background: var(--eflo-surface-2);
    border: 1px solid var(--eflo-border);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--eflo-text-3);
    font-size: 14px;
    transition: all 0.15s;
    flex-shrink: 0;
    padding: 0;
}

.ms-close:hover {
    background: var(--eflo-surface-3);
    color: var(--eflo-text);
}

.ms-tabs {
    display: flex;
    border-bottom: 1px solid var(--eflo-border);
    padding: 0 14px;
    flex-shrink: 0;
    background: var(--eflo-surface-2);
}

.mstab {
    flex: 1;
    padding: 10px 6px;
    text-align: center;
    font-size: 11.5px;
    font-weight: 500;
    color: var(--eflo-text-3);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: all 0.15s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.mstab:hover {
    color: var(--eflo-text-2);
}

.mstab.active {
    color: var(--eflo-blue);
    border-bottom-color: var(--eflo-blue);
    font-weight: 600;
}

.cnt {
    font-size: 9.5px;
    font-weight: 700;
    padding: 1px 5px;
    border-radius: 10px;
    background: var(--eflo-surface-3);
    color: var(--eflo-text-3);
}

.mstab.active .cnt {
    background: var(--eflo-blue-dim);
    color: var(--eflo-blue);
}

.cnt-red {
    background: var(--eflo-red-dim) !important;
    color: var(--eflo-red) !important;
}

.ms-read-filter {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-bottom: 1px solid var(--eflo-border);
    flex-shrink: 0;
    background: var(--eflo-surface);
}

.rf-btn {
    padding: 4px 12px;
    border-radius: 20px;
    background: var(--eflo-surface-2);
    border: 1px solid var(--eflo-border);
    font-size: 11.5px;
    font-weight: 500;
    color: var(--eflo-text-2);
    cursor: pointer;
    transition: all 0.15s;
    display: flex;
    align-items: center;
    gap: 5px;
    font-family: inherit;
}

.rf-btn:hover {
    border-color: var(--eflo-border-2);
}

.rf-btn.active {
    background: var(--eflo-blue-dim);
    border-color: rgba(0, 96, 240, 0.25);
    color: var(--eflo-blue);
    font-weight: 600;
}

.rf-badge {
    background: var(--eflo-red);
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    padding: 0 5px;
    border-radius: 10px;
    min-width: 16px;
    text-align: center;
}

.rf-tools {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 6px;
}

.rf-sort-select-wrap {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 8px;
    height: 28px;
    border-radius: 7px;
    border: 1px solid var(--eflo-border);
    background: var(--eflo-surface-2);
}

.rf-sort-icon {
    color: var(--eflo-text-3);
    font-size: 16px;
    line-height: 1;
}

.rf-sort-select {
    border: none;
    outline: none;
    background: transparent;
    color: var(--eflo-text-2);
    font-size: 11.5px;
    font-family: inherit;
    cursor: pointer;
}

.rf-icon-btn {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    border: 1px solid var(--eflo-border);
    background: var(--eflo-surface-2);
    color: var(--eflo-text-2);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s;
    font-size: 13px;
}

.rf-icon-btn .material-icons {
    font-size: 17px;
    line-height: 1;
}

.rf-icon-btn:hover:not(:disabled) {
    border-color: var(--eflo-border-2);
}

.rf-icon-btn.active {
    background: var(--eflo-blue-dim);
    border-color: rgba(0, 96, 240, 0.25);
}

.rf-icon-btn.danger:hover:not(:disabled) {
    background: var(--eflo-red-dim);
    border-color: rgba(240, 45, 45, 0.25);
}

.rf-icon-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.ms-search {
    padding: 10px 14px;
    border-bottom: 1px solid var(--eflo-border);
    flex-shrink: 0;
}

.ms-search input {
    width: 100%;
    background: var(--eflo-surface-2);
    border: 1px solid var(--eflo-border);
    border-radius: 7px;
    padding: 7px 12px 7px 32px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12px;
    color: var(--eflo-text);
    outline: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='%239ba8c4' stroke-width='2.5'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cline x1='21' y1='21' x2='16.65' y2='16.65'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: 10px center;
    transition: border-color 0.15s;
}

.ms-search input:focus {
    border-color: var(--eflo-blue);
    box-shadow: 0 0 0 3px var(--eflo-blue-dim);
}

.ms-list {
    flex: 1;
    overflow-y: auto;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 7px;
}

.ms-empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--eflo-text-3);
    font-size: 12px;
    font-style: italic;
    padding: 32px 0;
}

/* ══ MESSAGE CARDS ══ */
.mc {
    background: var(--eflo-surface);
    border: 1px solid var(--eflo-border);
    border-radius: 10px;
    padding: 12px 14px;
    cursor: pointer;
    transition: all 0.15s;
    box-shadow: var(--eflo-shadow-sm);
}

.mc:hover {
    border-color: var(--eflo-border-2);
    box-shadow: var(--eflo-shadow);
    transform: translateY(-1px);
}

.mc.mc-alert {
    border-left: 3px solid var(--eflo-red);
}

.mc.mc-warn {
    border-left: 3px solid var(--eflo-orange);
}

.mc.mc-unread {
    border-left: 3px solid var(--eflo-blue);
}

.mc.mc-selected {
    border-color: var(--eflo-blue);
    background: var(--eflo-blue-dim);
    box-shadow: 0 0 0 2px rgba(0, 96, 240, 0.18), var(--eflo-shadow);
    transform: translateY(-1px);
}

.mc.mc-selected .mc-subject {
    color: var(--eflo-blue);
}

.mc-head {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 7px;
}

.mc-avatar {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    flex-shrink: 0;
}

.mca-red {
    background: var(--eflo-red-dim);
    color: var(--eflo-red);
}

.mca-orange {
    background: var(--eflo-orange-dim);
    color: var(--eflo-orange);
}

.mca-blue {
    background: var(--eflo-blue-dim);
    color: var(--eflo-blue);
}

.mca-teal {
    background: var(--eflo-teal-dim);
    color: var(--eflo-teal);
}

.mca-purple {
    background: var(--eflo-purple-dim);
    color: var(--eflo-purple);
}

.mc-info {
    flex: 1;
    min-width: 0;
}

.mc-subject {
    font-size: 12.5px;
    font-weight: 600;
    color: var(--eflo-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.mc-meta {
    font-size: 11px;
    color: var(--eflo-text-3);
    margin-top: 2px;
    display: flex;
    gap: 6px;
}

.mc-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
    margin-right: 8px;
}

.mc-reply-badge {
    display: flex;
    align-items: center;
    gap: 3px;
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 600;
    color: var(--eflo-blue);
    background: var(--eflo-blue-dim);
    border: none;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.15s ease;
}

.mc-reply-badge:hover {
    background: var(--eflo-blue);
    color: #fff;
}

.mc-delete-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    font-size: 12px;
    color: var(--eflo-text-3);
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    opacity: 0;
    transition: all 0.15s ease;
}

.mc:hover .mc-delete-btn {
    opacity: 1;
}

.mc-delete-btn:hover {
    background: var(--eflo-red-dim);
    color: var(--eflo-red);
}

.mc-time {
    font-size: 11px;
    color: var(--eflo-text-3);
    white-space: nowrap;
    flex-shrink: 0;
}

.mc-excerpt {
    font-size: 12px;
    color: var(--eflo-text-2);
    line-height: 1.45;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 8px;
}

.mc-kpi-strip {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 6px;
}

.mc-kpi-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 168px;
    padding: 2px 8px;
    border-radius: 20px;
    font-size: 10px;
    line-height: 1.3;
    background: var(--eflo-surface-3);
    border: 1px solid var(--eflo-border);
}

.mc-kpi-pill-lbl {
    font-weight: 700;
    color: var(--eflo-navy);
    flex-shrink: 0;
}

.mc-kpi-pill-val {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--eflo-text-2);
    font-weight: 500;
}

.mc-footer {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-wrap: wrap;
}

/* ══ TAGS ══ */
.tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 20px;
}

.tg-alert {
    background: var(--eflo-red-dim);
    color: var(--eflo-red);
    border: 1px solid rgba(232, 48, 74, 0.18);
}

/* Élément supprimé : conservé pour la traçabilité, signalé en rouge */
.tg-deleted {
    background: var(--eflo-red);
    color: #fff;
    border: 1px solid var(--eflo-red);
}

.mc.mc-deleted {
    background: var(--eflo-red-dim);
    border-left: 3px solid var(--eflo-red);
}

.mc-deleted-note {
    margin-top: 4px;
    font-size: 11px;
    font-style: italic;
    color: var(--eflo-red);
}

.tg-warn {
    background: var(--eflo-orange-dim);
    color: var(--eflo-orange);
    border: 1px solid rgba(245, 168, 0, 0.25);
}

.tg-msg {
    background: var(--eflo-blue-dim);
    color: var(--eflo-blue);
    border: 1px solid rgba(26, 86, 255, 0.18);
}

.tg-memo {
    background: var(--eflo-teal-dim);
    color: var(--eflo-teal);
    border: 1px solid rgba(0, 168, 124, 0.18);
}

.tg-sms {
    background: var(--eflo-purple-dim);
    color: var(--eflo-purple);
    border: 1px solid rgba(124, 58, 237, 0.18);
}

.tg-sent {
    background: var(--eflo-surface-3);
    color: var(--eflo-text-3);
    border: 1px solid var(--eflo-border);
}

.tg-read {
    background: var(--eflo-green-dim);
    color: var(--eflo-green);
    border: 1px solid rgba(23, 160, 92, 0.2);
}

.tg-treated {
    background: var(--eflo-blue-dim);
    color: var(--eflo-blue);
    border: 1px solid rgba(26, 86, 255, 0.18);
}

.ms-new-btn {
    margin: 12px;
    flex-shrink: 0;
    padding: 11px;
    border-radius: 9px;
    color: #488df5;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12.5px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.15s;
    outline: none !important;
    background: none;
}

.ms-new-btn:hover {
    transform: translateY(-1px);
}

/* ══ DETAIL DRAWER ══ */
.eflo-detail-drawer {
    position: fixed;
    right: 0;
    top: 0;
    bottom: 0;
    width: 440px;
    background: var(--eflo-surface);
    border-left: 1px solid var(--eflo-border-2);
    box-shadow: -8px 0 32px rgba(24, 33, 58, 0.11);
    z-index: 3333;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transform: translateX(110%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.eflo-detail-drawer.open {
    transform: translateX(0);
}

.dd-header {
    padding: 14px 18px;
    border-bottom: 1px solid var(--eflo-border);
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
    background: var(--eflo-surface);
}

.dd-icon {
    font-size: 20px;
}

.dd-header-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.dd-subject {
    font-size: 13.5px;
    font-weight: 700;
    color: var(--eflo-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.dd-header-meta {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
}

.dd-thread {
    flex: 1;
    overflow-y: auto;
    padding: 16px 14px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: var(--eflo-surface-2);
    background-image: radial-gradient(circle at 1px 1px, var(--eflo-border) 1px, transparent 0);
    background-size: 20px 20px;
}

.dd-thread::-webkit-scrollbar {
    width: 4px;
}

.dd-thread::-webkit-scrollbar-track {
    background: transparent;
}

.dd-thread::-webkit-scrollbar-thumb {
    background: var(--eflo-border-2);
    border-radius: 4px;
}

.dd-date-sep {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 10px 0 6px;
}

.dd-date-sep span {
    font-size: 10.5px;
    font-weight: 600;
    color: var(--eflo-text-3);
    background: var(--eflo-surface-3);
    border: 1px solid var(--eflo-border);
    border-radius: 20px;
    padding: 3px 10px;
}

.dd-bubble-row {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    margin: 2px 0;
    max-width: 100%;
}

.bubble-row-me {
    flex-direction: row-reverse;
}

.bubble-row-them {
    flex-direction: row;
}

.dd-bubble-avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9.5px;
    font-weight: 800;
    flex-shrink: 0;
    margin-bottom: 2px;
    letter-spacing: -0.5px;
}

.bav-red {
    background: var(--eflo-red-dim);
    color: var(--eflo-red);
    border: 1px solid rgba(232, 48, 74, 0.2);
}

.bav-teal {
    background: var(--eflo-teal-dim);
    color: var(--eflo-teal);
    border: 1px solid rgba(0, 196, 154, 0.2);
}

.bav-purple {
    background: var(--eflo-purple-dim);
    color: var(--eflo-purple);
    border: 1px solid rgba(124, 58, 237, 0.2);
}

.bav-blue {
    background: var(--eflo-blue-dim);
    color: var(--eflo-blue);
    border: 1px solid rgba(0, 96, 240, 0.2);
}

.bav-gray {
    background: var(--eflo-surface-3);
    color: var(--eflo-text-3);
    border: 1px solid var(--eflo-border);
}

.dd-bubble-wrap {
    display: flex;
    flex-direction: column;
    max-width: 76%;
    min-width: 0;
}

.dd-bubble-meta-top {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-bottom: 3px;
    font-size: 10.5px;
}

.dd-bubble-meta-top.align-right {
    justify-content: flex-end;
}

.dd-sender-name {
    font-weight: 700;
    color: var(--eflo-text-2);
    font-size: 10.5px;
}

.dd-event-badge {
    display: inline-flex;
    align-items: center;
    font-size: 9.5px;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 10px;
}

.ev-reply {
    background: var(--eflo-blue-dim);
    color: var(--eflo-blue);
    border: 1px solid rgba(0, 96, 240, 0.18);
}

.ev-transfer {
    background: var(--eflo-purple-dim);
    color: var(--eflo-purple);
    border: 1px solid rgba(124, 58, 237, 0.18);
}

.dd-bubble-ref {
    display: flex;
    gap: 8px;
    padding: 6px 10px;
    background: rgba(0, 0, 0, 0.04);
    border-radius: 7px 7px 0 0;
    margin-bottom: -4px;
    border-bottom: none;
    font-size: 11px;
}

.bubble-me .dd-bubble-ref {
    background: rgba(255, 255, 255, 0.15);
}

.dd-bubble-ref-bar {
    width: 3px;
    border-radius: 3px;
    background: var(--eflo-blue);
    flex-shrink: 0;
}

.dd-bubble-ref-label {
    font-size: 9.5px;
    font-weight: 700;
    color: var(--eflo-blue);
    margin-bottom: 1px;
}

.dd-bubble-ref-text {
    font-size: 11px;
    color: var(--eflo-text-2);
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
}

.dd-bubble {
    border-radius: 16px;
    padding: 9px 13px;
    box-shadow: 0 1px 3px rgba(0, 7, 63, 0.08);
    position: relative;
    line-height: 1.5;
}

.bubble-them {
    background: var(--eflo-surface);
    border: 1px solid var(--eflo-border);
    border-bottom-left-radius: 4px;
    color: var(--eflo-text);
}

.bubble-me {
    background: var(--eflo-navy);
    border-bottom-right-radius: 4px;
    color: #fff;
}

.bubble-alert {
    background: var(--eflo-red-dim) !important;
    border: 1px solid rgba(232, 48, 74, 0.25) !important;
    color: var(--eflo-red) !important;
}

.dd-bubble-body {
    font-size: 12.5px;
    white-space: pre-wrap;
    word-break: break-word;
}

.dd-bubble-attachments {
    margin-top: 7px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    border-top: 1px solid rgba(255, 255, 255, 0.15);
    padding-top: 6px;
}

.bubble-them .dd-bubble-attachments {
    border-top-color: var(--eflo-border);
}

.dd-bubble-doc {
    font-size: 11px;
    display: flex;
    align-items: center;
    gap: 5px;
    opacity: 0.8;
}

.dd-bubble-time {
    font-size: 10px;
    color: var(--eflo-text-3);
    margin-top: 3px;
    display: flex;
    align-items: center;
    gap: 3px;
}

.dd-bubble-time.time-right {
    justify-content: flex-end;
}

.dd-read-tick {
    font-size: 10px;
    color: var(--eflo-text-3);
    font-weight: 700;
}

.tick-read {
    color: var(--eflo-teal);
}

.dd-thread-loading {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 40px 0;
    color: var(--eflo-text-3);
    font-size: 12px;
    font-style: italic;
}

.dd-loading-spinner {
    width: 24px;
    height: 24px;
    border: 2px solid var(--eflo-border);
    border-top-color: var(--eflo-teal);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

.dd-thread-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: var(--eflo-text-3);
    font-size: 12px;
    font-style: italic;
    padding: 40px 0;
}

.dd-thread-empty-icon {
    font-size: 32px;
    opacity: 0.4;
}

.dd-actions-bar {
    padding: 12px 16px;
    border-top: 1px solid var(--eflo-border);
    background: var(--eflo-surface);
    display: flex;
    flex-direction: column;
    gap: 7px;
    flex-shrink: 0;
}

.dd-actions-row {
    display: flex;
    gap: 7px;
}

.dd-action-btn {
    flex: 1;
    padding: 8px;
    border-radius: 8px;
    font-size: 11.5px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    cursor: pointer;
    border: 1px solid var(--eflo-border);
    background: var(--eflo-surface-2);
    color: var(--eflo-text-2);
    transition: all 0.15s;
    text-align: center;
    font-weight: 600;
}

.dd-action-btn:hover:not(:disabled) {
    color: var(--eflo-text);
    border-color: var(--eflo-border-2);
    background: var(--eflo-surface-3);
}

.dd-action-btn.primary {
    background: rgb(33 150 243 / 13%);
    border-color: var(--primary);
    color: var(--primary);
}

.dd-action-btn.primary:hover:not(:disabled) {
    background: var(--primary);
    color: #fff;
}

.dd-action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* ══ COMPOSE MODAL ══ */
.eflo-overlay {
    position: fixed;
    inset: 0;
    background: rgba(24, 33, 58, 0.45);
    backdrop-filter: blur(4px);
    z-index: 4444;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s;
}

.eflo-overlay.open {
    opacity: 1;
    pointer-events: all;
}

.eflo-compose {
    background: var(--eflo-surface);
    border-radius: 16px;
    /* ── responsive width ── */
    width: 85vw;
    max-width: 1200px;
    max-height: 88vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0, 7, 63, 0.16);
    border: 1px solid var(--eflo-border-2);
    transform: translateY(16px) scale(0.98);
    transition: transform 0.22s;
    position: relative;
}

.eflo-overlay.open .eflo-compose {
    transform: translateY(0) scale(1);
}

/* ── Email popup ── */
.mca-indigo {
    background: rgba(79, 70, 229, 0.12);
    color: #4f46e5;
}

.tg-email {
    background: rgba(79, 70, 229, 0.12);
    color: #4f46e5;
    border: 1px solid rgba(79, 70, 229, 0.18);
}

.eflo-email-modal {
    background: var(--eflo-surface);
    border-radius: 16px;
    width: 70vw;
    max-width: 860px;
    max-height: 88vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 7, 63, 0.16);
    border: 1px solid var(--eflo-border-2);
    transform: translateY(16px) scale(0.98);
    transition: transform 0.22s;
}

.eflo-overlay.open .eflo-email-modal {
    transform: translateY(0) scale(1);
}

.em-header {
    padding: 16px 20px;
    border-bottom: 1px solid var(--eflo-border);
    display: flex;
    align-items: center;
    gap: 12px;
}

.em-icon {
    font-size: 20px;
}

.em-header-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
}

.em-subject {
    font-size: 14px;
    font-weight: 700;
    color: var(--eflo-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.em-ref {
    font-size: 11px;
    color: var(--eflo-accent-2);
    font-weight: 600;
}

.em-fields {
    padding: 12px 20px;
    border-bottom: 1px solid var(--eflo-border);
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: var(--eflo-surface-2);
}

.em-field {
    display: flex;
    gap: 8px;
    font-size: 12.5px;
    line-height: 1.5;
}

.em-lbl {
    flex: 0 0 44px;
    color: var(--eflo-text-3);
    font-weight: 600;
}

.em-val {
    color: var(--eflo-text);
    word-break: break-word;
}

.em-body {
    flex: 1;
    min-height: 240px;
    overflow: auto;
    padding: 0;
}

.em-iframe {
    width: 100%;
    height: 100%;
    min-height: 360px;
    border: 0;
    background: #fff;
}

.em-plain {
    margin: 0;
    padding: 20px;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: inherit;
    font-size: 13px;
    color: var(--eflo-text);
}

.cm-header {
    padding: 16px 20px;
    border-bottom: 1px solid var(--eflo-border);
    display: flex;
    align-items: center;
    gap: 12px;
    position: sticky;
    top: 0;
    background: var(--eflo-surface);
    z-index: 1;
    border-radius: 16px 16px 0 0;
}

.cm-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
    flex-shrink: 0;
}

.cm-title {
    font-size: 15px;
    font-weight: 700;
    flex: 1;
    color: var(--eflo-text);
}

.cm-close {
    width: 30px;
    height: 30px;
    border-radius: 7px;
    background: var(--eflo-surface-2);
    border: 1px solid var(--eflo-border);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--eflo-text-3);
    font-size: 15px;
    transition: all 0.15s;
    padding: 0;
}

.cm-close:hover {
    color: var(--eflo-text);
}

/* Important toggle */
.important-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    cursor: pointer;
    border: 1.5px solid var(--eflo-border);
    background: var(--eflo-surface-2);
    transition: all 0.2s;
    user-select: none;
    flex-shrink: 0;
}

.important-toggle:hover {
    border-color: var(--eflo-border-2);
}

.important-toggle.on {
    background: var(--eflo-red-dim);
    border-color: rgba(232, 48, 74, 0.3);
}

.important-flag {
    font-size: 13px;
    line-height: 1;
}

.important-label {
    font-size: 11.5px;
    font-weight: 600;
    color: var(--eflo-text-2);
}

.important-toggle.on .important-label {
    color: var(--eflo-red);
}

.toggle-track {
    width: 28px;
    height: 16px;
    border-radius: 10px;
    background: var(--eflo-border-2);
    position: relative;
    transition: background 0.2s;
    flex-shrink: 0;
}

.toggle-thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    transition: left 0.2s;
}

.important-toggle.on .toggle-track {
    background: var(--eflo-red);
}

.important-toggle.on .toggle-thumb {
    left: 14px;
}

/* ══ 2-COLUMN BODY ══ */
.cm-body {
    padding: 20px 24px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
    align-items: start;
}

.cm-col {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

/* Tablet portrait & mobile → 1 colonne */
@media (max-width: 768px) {
    .eflo-compose {
        width: calc(100vw - 24px);
        max-height: 95vh;
    }

    .cm-body {
        grid-template-columns: 1fr;
        padding: 16px;
        gap: 14px;
    }

    /* Sur mobile : droite passe après gauche → on réordonne avec order */
    .cm-col-right {
        order: -1;
    }
}

/* Tablet paysage */
@media (min-width: 769px) and (max-width: 1024px) {
    .eflo-compose {
        width: 92vw;
    }
}

/* ══ FORM ELEMENTS ══ */
.form-sec {
    border: 1px solid var(--eflo-border);
    border-radius: 10px;
    overflow: unset;
}

.form-sec-title {
    padding: 9px 14px;
    background: var(--eflo-surface-2);
    border-bottom: 1px solid var(--eflo-border);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.9px;
    text-transform: uppercase;
    color: var(--eflo-text-3);
    display: flex;
    align-items: center;
    gap: 7px;
    border-radius: 10px 10px 0 0;
}

.fst-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
}

.form-sec-body {
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 11px;
}

.form-row {
    display: flex;
    gap: 11px;
}

.form-g {
    display: flex;
    flex-direction: column;
    gap: 5px;
    flex: 1;
}

.form-lbl {
    font-size: 11.5px;
    font-weight: 600;
    color: var(--eflo-text-2);
    display: flex;
    align-items: center;
    gap: 4px;
}

.req {
    color: var(--eflo-red);
    font-size: 10px;
}

/* Subject select */
.fselect-wrap {
    position: relative;
    display: flex;
    align-items: center;
}

.fselect {
    width: 100%;
    background: var(--eflo-surface-2);
    border: 1px solid var(--eflo-border);
    border-radius: 7px;
    color: var(--eflo-text);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12.5px;
    padding: 8px 32px 8px 11px;
    outline: none;
    transition: all 0.15s;
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='%239ba8c4' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
}

.fselect:focus {
    border-color: var(--eflo-blue);
    box-shadow: 0 0 0 3px var(--eflo-blue-dim);
}

.fselect-clear {
    position: absolute;
    right: 28px;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--eflo-text-3);
    font-size: 12px;
    padding: 0 4px;
}

.fselect-clear:hover {
    color: var(--eflo-red);
}

/* Inputs */
.finput {
    width: 100%;
    background: var(--eflo-surface-2);
    border: 1px solid var(--eflo-border);
    border-radius: 7px;
    color: var(--eflo-text);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12.5px;
    padding: 8px 11px;
    outline: none;
    transition: all 0.15s;
}

.finput:focus {
    border-color: var(--eflo-blue);
    box-shadow: 0 0 0 3px var(--eflo-blue-dim);
}

/* Textarea */
.ftextarea {
    width: 100%;
    background: var(--eflo-surface-2);
    border: 1px solid var(--eflo-border);
    border-radius: 7px;
    color: var(--eflo-text);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12.5px;
    padding: 8px 11px;
    outline: none;
    transition: all 0.15s;
    resize: vertical;
    min-height: 120px;
    line-height: 1.55;
}

.ftextarea:focus {
    border-color: var(--eflo-blue);
    box-shadow: 0 0 0 3px var(--eflo-blue-dim);
}

/* Reference autocomplete */
.ref-search-wrap {
    position: relative;
}

.ref-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: var(--eflo-surface);
    border: 1px solid var(--eflo-border-2);
    border-radius: 8px;
    box-shadow: var(--eflo-shadow);
    z-index: 20;
    max-height: 200px;
    overflow-y: auto;
}

.ref-opt {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    font-size: 12px;
    cursor: pointer;
    transition: background 0.1s;
}

.ref-opt:hover {
    background: var(--eflo-surface-2);
}

.ref-opt.ref-empty,
.ref-opt.ref-loading {
    color: var(--eflo-text-3);
    font-style: italic;
    cursor: default;
}

.ref-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 7px;
    border-radius: 5px;
    font-size: 9.5px;
    font-weight: 700;
    white-space: nowrap;
    flex-shrink: 0;
}

.ref-badge-party {
    background: var(--eflo-teal-dim);
    color: var(--eflo-teal);
    border: 1px solid rgba(0, 196, 154, 0.25);
}

.ref-badge-order {
    background: var(--eflo-blue-dim);
    color: var(--eflo-blue);
    border: 1px solid rgba(0, 96, 240, 0.2);
}

.ref-badge-offer {
    background: var(--eflo-purple-dim);
    color: var(--eflo-purple);
    border: 1px solid rgba(124, 58, 237, 0.2);
}

.ref-id {
    color: var(--eflo-text);
    font-weight: 500;
}

.ref-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
}

.ref-chip {
    display: flex;
    align-items: center;
    gap: 5px;
    background: var(--eflo-surface-2);
    border: 1px solid var(--eflo-border-2);
    border-radius: 6px;
    padding: 4px 8px;
    font-size: 11.5px;
}

.ref-chip-remove {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--eflo-text-3);
    font-size: 10px;
    padding: 0 2px;
}

.ref-chip-remove:hover {
    color: var(--eflo-red);
}

/* Recipients */
.recip-filter-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
}

.recip-filter-tab {
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    background: var(--eflo-surface-2);
    color: var(--eflo-text-3);
    transition: all 0.15s;
    border: 1px solid transparent;
}

.recip-filter-tab:hover {
    color: var(--eflo-text-2);
}

.recip-filter-tab.active {
    background: var(--eflo-blue-dim);
    color: var(--eflo-blue);
    border-color: rgba(26, 86, 255, 0.25);
}

.recip-search-wrap {
    position: relative;
}

.recip-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: var(--eflo-surface);
    border: 1px solid var(--eflo-border-2);
    border-radius: 8px;
    box-shadow: var(--eflo-shadow);
    z-index: 20;
    max-height: 200px;
    overflow-y: auto;
}

.recip-opt {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    font-size: 12px;
    cursor: pointer;
    transition: background 0.1s;
}

.recip-opt:hover {
    background: var(--eflo-surface-2);
}

.recip-opt.recip-empty {
    color: var(--eflo-text-3);
    font-style: italic;
    cursor: default;
}

.recip-intervenants-strip {
    margin-bottom: 10px;
    padding: 10px 10px 6px;
    border: 1px solid var(--eflo-border);
    border-radius: 8px;
    background: var(--eflo-surface-2);
}

.recip-intervenants-strip-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--eflo-text-2);
    margin-bottom: 4px;
}

.recip-intervenant-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
    padding: 8px 0;
    border-top: 1px solid var(--eflo-border);
}

.recip-intervenants-strip .recip-intervenant-card:first-of-type {
    border-top: none;
    padding-top: 2px;
}

.recip-intervenant-card-info {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    min-width: 0;
    flex: 1;
}

.recip-intervenant-card-cta {
    display: flex;
    flex-shrink: 0;
    gap: 6px;
}

.recip-cta {
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid transparent;
    white-space: nowrap;
}

.recip-cta-message {
    background: var(--eflo-blue-dim);
    color: var(--eflo-blue);
    border-color: rgba(26, 86, 255, 0.25);
}

.recip-cta-mail {
    background: #fef3e2;
    color: #9a4f10;
    border-color: rgba(230, 126, 34, 0.25);
}

.recip-opt-simple {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
    cursor: pointer;
}

.recip-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 7px;
    border-radius: 5px;
    font-size: 9.5px;
    font-weight: 700;
    white-space: nowrap;
    flex-shrink: 0;
}

.recip-badge-user {
    background: var(--eflo-blue-dim);
    color: var(--eflo-blue);
    border: 1px solid rgba(26, 86, 255, 0.2);
}

.recip-badge-group {
    background: var(--eflo-teal-dim);
    color: var(--eflo-teal);
    border: 1px solid rgba(0, 196, 154, 0.25);
}

.recip-badge-email {
    background: #fef3e2;
    color: #9a4f10;
    border: 1px solid rgba(230, 126, 34, 0.25);
}

.recip-badge-order-party {
    background: var(--eflo-teal-dim);
    color: #006b52;
    border: 1px solid rgba(0, 196, 154, 0.35);
}

.recip-badge-default {
    background: var(--eflo-surface-2);
    color: var(--eflo-text-2);
    border: 1px solid var(--eflo-border);
}

.recip-name {
    color: var(--eflo-text);
    font-weight: 500;
}

.recip-meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.recip-sub {
    font-size: 11px;
    color: var(--eflo-text-2);
}

.recip-sub-missing {
    color: var(--eflo-orange);
    font-style: italic;
}

.recip-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
}

.recip-chip {
    display: flex;
    align-items: center;
    gap: 5px;
    background: var(--eflo-surface-2);
    border: 1px solid var(--eflo-border-2);
    border-radius: 6px;
    padding: 4px 8px;
    font-size: 11.5px;
}

.recip-chip-remove {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--eflo-text-3);
    font-size: 10px;
    padding: 0 2px;
}

.recip-chip-remove:hover {
    color: var(--eflo-red);
}

.recip-empty-msg {
    font-size: 11.5px;
    color: var(--eflo-text-3);
    font-style: italic;
    padding: 8px 0;
}

/* Type toggle */
.type-toggle {
    display: flex;
    background: var(--eflo-surface-2);
    border: 1px solid var(--eflo-border);
    border-radius: 8px;
    padding: 3px;
    gap: 2px;
}

.tt-opt {
    flex: 1;
    padding: 7px 8px;
    border-radius: 6px;
    text-align: center;
    font-size: 11.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
    color: var(--eflo-text-3);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
}

.tt-opt:hover {
    color: var(--eflo-text-2);
}

.tt-opt.active {
    background: var(--eflo-surface);
    color: var(--eflo-text);
    box-shadow: var(--eflo-shadow-sm);
}

/* Alert labels */
.alert-labels {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.al {
    padding: 5px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    border: 1.5px solid transparent;
    transition: all 0.15s;
}

.al:hover {
    opacity: 0.85;
}

.al-default {
    background: var(--eflo-surface-3);
    color: var(--eflo-text-2);
    border-color: var(--eflo-border);
}

.al-default.on {
    background: var(--eflo-text-2);
    color: #fff;
}

.al-retard {
    background: var(--eflo-red-dim);
    color: var(--eflo-red);
    border-color: rgba(232, 48, 74, 0.2);
}

.al-retard.on {
    background: var(--eflo-red);
    color: #fff;
}

.al-paiement {
    background: var(--eflo-orange-dim);
    color: var(--eflo-orange);
    border-color: rgba(245, 168, 0, 0.25);
}

.al-paiement.on {
    background: var(--eflo-orange);
    color: #fff;
}

.al-doc {
    background: var(--eflo-blue-dim);
    color: var(--eflo-blue);
    border-color: rgba(26, 86, 255, 0.2);
}

.al-doc.on {
    background: var(--eflo-blue);
    color: #fff;
}

.al-liv {
    background: var(--eflo-teal-dim);
    color: var(--eflo-teal);
    border-color: rgba(0, 168, 124, 0.2);
}

.al-liv.on {
    background: var(--eflo-teal);
    color: #fff;
}

.al-relance {
    background: var(--eflo-purple-dim);
    color: var(--eflo-purple);
    border-color: rgba(124, 58, 237, 0.2);
}

.al-relance.on {
    background: var(--eflo-purple);
    color: #fff;
}

/* Context banner */
.compose-context-banner {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-radius: 7px;
    background: var(--eflo-blue-dim);
    border: 1px solid rgba(0, 96, 240, 0.2);
    font-size: 12px;
    color: var(--eflo-blue);
}

.context-clear {
    margin-left: auto;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--eflo-text-3);
    font-size: 12px;
    padding: 0;
}

.context-clear:hover {
    color: var(--eflo-text);
}

/* ══ DOC SEARCH + SCROLL LIST ══ */
.doc-search-wrap {
    position: relative;
}

.doc-items-scroll {
    max-height: 220px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 5px;
    border: 1px solid var(--eflo-border);
    border-radius: 8px;
    padding: 6px;
    background: var(--eflo-surface-2);
}

.doc-items-scroll::-webkit-scrollbar {
    width: 4px;
}

.doc-items-scroll::-webkit-scrollbar-track {
    background: transparent;
}

.doc-items-scroll::-webkit-scrollbar-thumb {
    background: var(--eflo-border-2);
    border-radius: 4px;
}

.doc-item {
    display: flex;
    align-items: center;
    gap: 9px;
    background: var(--eflo-surface);
    border: 1px solid var(--eflo-border);
    border-radius: 7px;
    padding: 8px 11px;
    cursor: pointer;
    transition: all 0.15s;
    font-size: 12px;
    flex-shrink: 0;
}

.doc-item:hover {
    border-color: var(--eflo-border-2);
    background: var(--eflo-surface-3);
}

.doc-item.on {
    border-color: rgba(0, 96, 240, 0.3);
    background: var(--eflo-blue-dim);
}

.doc-item-info {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
}

.doc-name {
    font-weight: 500;
    color: var(--eflo-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.doc-process {
    font-size: 10.5px;
    color: var(--eflo-text-3);
    margin-top: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.doc-item.on .doc-name {
    color: var(--eflo-blue);
}

.doc-chk {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    border: 1.5px solid var(--eflo-border-2);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 10px;
    font-weight: 700;
    transition: all 0.15s;
}

.doc-item.on .doc-chk {
    background: var(--eflo-blue);
    border-color: var(--eflo-blue);
    color: #fff;
}

.doc-empty {
    font-size: 11.5px;
    color: var(--eflo-text-3);
    font-style: italic;
    text-align: center;
    padding: 12px 0;
}

.file-hint {
    margin-top: 6px;
    font-size: 11px;
    color: var(--eflo-text-3);
}

/* Sending overlay */
.cm-sending {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(3px);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
}

.cm-sending-inner {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    font-weight: 600;
    color: var(--eflo-text-2);
}

.cm-spinner {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2.5px solid var(--eflo-border-2);
    border-top-color: var(--eflo-blue);
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* Footer */
.cm-footer {
    padding: 14px 24px;
    border-top: 1px solid var(--eflo-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border-radius: 0 0 16px 16px;
    position: sticky;
    bottom: 0;
    background: var(--eflo-surface);
}

.cm-footer-left {
    flex: 1;
}

.cm-footer-right {
    display: flex;
    gap: 9px;
}

.cm-error {
    font-size: 11.5px;
    color: var(--eflo-red);
    font-weight: 500;
}

.btn-cancel {
    padding: 8px 16px;
    border-radius: 7px;
    background: var(--eflo-surface-2);
    border: 1px solid var(--eflo-border);
    color: var(--eflo-text-2);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12.5px;
    cursor: pointer;
    transition: all 0.15s;
}

.btn-cancel:hover {
    color: var(--eflo-text);
}

.btn-send {
    padding: 8px 20px;
    border-radius: 7px;
    background: var(--eflo-blue);
    border: none;
    color: #fff;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12.5px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 7px;
    transition: all 0.15s;
    box-shadow: 0 2px 10px rgba(0, 96, 240, 0.3);
}

.btn-send:hover:not(:disabled) {
    background: var(--eflo-blue-light);
    transform: translateY(-1px);
}

.btn-send.alert-mode {
    background: var(--eflo-red);
    box-shadow: 0 2px 10px rgba(232, 48, 74, 0.3);
}

.btn-send.important-mode {
    background: var(--eflo-navy);
    box-shadow: 0 2px 10px rgba(0, 7, 63, 0.3);
}

.btn-send.alert-mode.important-mode {
    background: var(--eflo-red);
}

.btn-send:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

/* Subtitle link */
.ms-subtitle-link {
    cursor: pointer;
    transition: color 0.15s;
    display: flex;
    align-items: center;
    gap: 4px;
}

.ms-subtitle-link:hover {
    color: var(--eflo-accent);
}

.ms-subtitle-ext {
    font-size: 9px;
    opacity: 0.7;
}

/* Scrollbars */
.ms-list::-webkit-scrollbar,
.dd-body::-webkit-scrollbar,
.eflo-compose::-webkit-scrollbar {
    width: 5px;
}

.ms-list::-webkit-scrollbar-track,
.dd-body::-webkit-scrollbar-track,
.eflo-compose::-webkit-scrollbar-track {
    background: transparent;
}

.ms-list::-webkit-scrollbar-thumb,
.dd-body::-webkit-scrollbar-thumb,
.eflo-compose::-webkit-scrollbar-thumb {
    background: var(--eflo-border-2);
    border-radius: 4px;
}
</style>
